import { WidgetApi } from './api';
import { HumanChatChannel, type HumanChatState } from './humanChat';
import { humanChatLabel } from './humanChatLabels';
import { HumanChatRecoveryStore } from './humanChatRecovery';
import { HumanChatSessionLock } from './humanChatSessionLock';
import type { HumanChatSendRetry } from './humanChatSendRetry';
import type { HumanChatCommandRetry } from './humanChatCommandRetry';
import { WidgetTransport } from './transport';
import { WidgetAudio, pcm16Base64ChunksToWavUrl } from './audio';
import { WidgetUi } from './ui';
import { widgetErrorMessage } from './errors';
import { createWorkingFillerController, resolveWorkingDelayPhrase, type WorkingFillerController } from '../../iva-runtime/shared/workingFiller';
import type {
  WidgetPublicConfig,
  WidgetModality,
  IssueJwtResponse,
  StartSessionResponse,
  WidgetTransferDirective,
  NearRealtimeTurnResponse,
  WidgetPill,
  WidgetPrecallForm,
} from './types';

/** Schemes a pill may open. Mirrors the server-side allow-list. */
const PILL_URL_SCHEMES = ['http:', 'https:', 'tel:', 'mailto:', 'sms:', 'whatsapp:'];

/** Silence after playback drains before the hands-free mic is restored locally. */
const MIC_RESTORE_GRACE_MS = 1500;

function isSafePillUrl(raw: string | undefined): boolean {
  if (!raw) return false;
  try {
    return PILL_URL_SCHEMES.indexOf(new URL(raw).protocol) >= 0;
  } catch {
    return false;
  }
}

/**
 * Opens a pill destination. An anchor click is used instead of `window.open`
 * because embedded/sandboxed hosts (the admin tester included) block scripted
 * popups, which silently degrades into navigating the host frame away.
 */
function openExternal(url: string, target?: '_blank' | '_self'): void {
  const a = document.createElement('a');
  a.href = url;
  a.target = target || '_blank';
  a.rel = 'noopener noreferrer';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Public loader entry. The bundle is shipped as an IIFE so the global
// AutoQMVirtualAgent.init() can be invoked from inline script tags OR
// automatically by the auto-init hook below.

export interface InitOptions {
  /** Public key from the widget admin UI. */
  publicKey: string;
  /** Embed secret pairing with the public key — required to mint a session JWT.
   *  Optional when `devTokenResponse` is provided (tester / admin preview). */
  embedSecret?: string;
  language?: string;
  /** Base URL of the Function App (e.g. https://app.example.com). Defaults to current origin. */
  apiBaseUrl?: string;
  /** Preferred modality. Falls back to the widget's default modality if not allowed. */
  modality?: WidgetModality;
  /**
   * Pre-issued widget session JWT (from `issueWidgetDevToken`) used by the
   * admin tester to bypass the public embedSecret + Origin checks. When set,
   * the SDK skips `issueSessionJwt` and goes straight to `startSession`.
   */
  devTokenResponse?: IssueJwtResponse;
  /**
   * Explicit origin of the admin tester host page. The tester renders the
   * widget inside a sandboxed `srcdoc` iframe whose own `location.origin`
   * is unreliable (`about:srcdoc` reports `"null"`), which silently drops
   * relay postMessages. When provided, the SDK targets this origin for the
   * relay channel and validates inbound control messages against it.
   */
  testerOrigin?: string;
}

class WidgetController {
  private startupAttempt: object | null = null;
  private readonly api: WidgetApi;
  private cfg: WidgetPublicConfig | null = null;
  private ui: WidgetUi | null = null;
  private transport: WidgetTransport | null = null;
  private audio: WidgetAudio | null = null;
  private humanChat: HumanChatChannel | null = null;
  private humanMode = false;
  private humanPreparing = false;
  private humanPreparationTimer: ReturnType<typeof setTimeout> | null = null;
  private humanPreparationAttempts = 0;
  private humanStarting = false;
  private humanSending = false;
  private humanEndCommand: string | null = null;
  private humanCancelCommand: string | null = null;
  private humanCancelBusy = false;
  private humanEndBusy = false;
  private humanReturning = false;
  private humanState: HumanChatState | null = null;
  private recoveredConversationId: string | null = null;
  private humanReloadRecovery = false;
  private humanDraft = '';
  private humanSendRetry: HumanChatSendRetry | undefined;
  private humanCommandRetries: HumanChatCommandRetry[] = [];
  private session: { token: string; sid: string; callId: string; modality: WidgetModality; nearRealtime?: boolean; pushToTalk?: boolean; captureMode?: 'push-to-talk' | 'continuous' } | null = null;
  /** Snapshot of the most recent session kept after teardown so postcall submit has a token. */
  private lastSession: { token: string; sid: string; callId: string; modality: WidgetModality; nearRealtime?: boolean; pushToTalk?: boolean; captureMode?: 'push-to-talk' | 'continuous' } | null = null;
  /** Realtime hold-to-talk: true while the caller is holding the mic button. */
  private micHeld = false;
  /**
   * Realtime voice: true once at least one audio frame was captured in the
   * current turn. A commit over an EMPTY input buffer errors server-side and
   * yields no reply (silent failure) — guard the commit on this flag.
   */
  private capturedThisTurn = false;
  /**
   * Serialized sender for realtime voice ingest (audio frames + commit). Audio
   * frames and the push-to-talk commit are otherwise independent fire-and-forget
   * POSTs that can race — a commit landing before its trailing audio appends
   * yields an empty input buffer and NO response. Chaining guarantees order.
   */
  private audioSendChain: Promise<unknown> = Promise.resolve();
  /** Continuous (open-mic) realtime voice: true while the agent is verbalizing. */
  private agentSpeaking = false;
  /** Continuous: the server signalled the agent response is done (response.done). */
  private agentResponseDone = false;
  /** Continuous: local agent audio playback has drained (queue empty). */
  private playbackDrained = false;
  /** Continuous: pending local turn-end fallback when the server's never arrives. */
  private micRestoreTimer: number | null = null;
  /** Continuous: the visitor's intended mute state (independent of agent-speech auto-mute). */
  private userMicMuted = true;
  private partialAgent = '';
  /**
   * Realtime voice: PCM `audio.delta` chunks accumulated for the CURRENT response
   * ONLY — used to build a single replayable voice clip attached to the agent
   * bubble when the response finalizes. Playback itself streams each delta
   * directly into the worklet (low latency); this buffer never gates playback.
   * Discarded when a continuation supersedes the response (a deferral on a tool turn).
   */
  private agentRespClipChunks: string[] = [];
  /**
   * Realtime voice: true while an agent response is generating server-side
   * (between response start and its cost.snapshot). Push-to-talk barge-in uses
   * it to CANCEL the in-flight response — otherwise a half-generated greeting/
   * reply keeps streaming and its audio interleaves with the next response,
   * producing garbled overlap.
   */
  private agentResponseActive = false;
  /**
   * Realtime voice: true once audio for the CURRENT response has started
   * streaming (first `audio.delta`) until it drains / is interrupted. Drives the
   * icon-only ■ STOP button on the agent bubble in every capture mode.
   */
  private agentAudioActive = false;
  /**
   * Realtime voice: after a barge-in/stop the cancelled server response keeps
   * emitting trailing `audio.delta` chunks for a beat. Only THAT response's
   * audio is dropped — keying on the id matters because transcription lags the
   * model, so a blanket flag cleared by the user's transcript would swallow the
   * opening of the next reply.
   */
  private droppedResponseId: string | null = null;
  /** `response.id` of the agent audio currently streaming in. */
  private currentAudioResponseId: string | null = null;
  /** Number of attachments currently uploading — while > 0 the send button stays disabled. */
  private uploadsInFlight = 0;
  /** Ready attachments queued to be sent with the next message, then cleared. */
  private pendingFiles: Array<{ id: string; name: string; mimeType: string; size: number }> = [];
  /** Local ids the visitor removed while still uploading — their result is discarded. */
  private removedUploads = new Set<string>();
  /** id of the last appended agent message, so streamed reply audio can attach to it. */
  private lastAgentMsgId: string | null = null;
  /** Transcript-email answers awaiting delivery once the conversation is over. */
  private pendingTranscriptRequests: Array<{ fieldId: string; email: string; source: 'pre' | 'post' }> = [];
  /** Guards concludeConversation against re-entry (user button + bridge close can race). */
  private concluding = false;
  /** True once the current conversation has been concluded; reset on the next start. */
  private concluded = false;
  /** Realtime push-to-talk: captured caller PCM chunks for the current turn's voice clip. */
  private userClipChunks: string[] = [];
  /** Pending caller voice-clip URL awaiting the matching spoken transcript turn. */
  private pendingUserClipUrl: string | null = null;
  /** Current near-RT / greeting URL-audio element, kept so a new clip can stop the previous one. */
  private urlAudio: HTMLAudioElement | null = null;
  /** True only while the URL-audio element is owned by a working filler event. */
  private workingFillerAudioActive = false;
  private nearRealtimeWorkingFiller: StartSessionResponse['workingFiller'] | null = null;
  private localWorkingFiller: WorkingFillerController | null = null;
  private pendingAfterFiller: Array<() => void> = [];
  private workingFillerIsSpeech = false;
  private fillerFinishTimer: ReturnType<typeof setTimeout> | null = null;
  private playbackSequence = 0;
  /** WebAudio graph used to read the played clip's level for the voice avatar. */
  private urlAudioCtx: AudioContext | null = null;
  private urlAudioRaf = 0;
  /** True once an agent.text.done committed a partial in the current realtime turn. */
  private committedAgentThisTurn = false;
  /** Realtime text: the agent is generating or running a tool (drives the typing dots). */
  private agentBusy = false;
  /** Realtime text: dispatched tools awaiting a result — keeps typing on during long tool calls. Keyed by toolCallId so duplicate results are idempotent. */
  private toolsInFlightIds = new Set<string>();
  /**
   * Self-healing typing-indicator watchdog (realtime text only). Any agent
   * activity (text delta, tool call/result, turn boundary) calls bumpTyping(),
   * which shows the dots and (re)arms this idle timer. When the timer fires with
   * NO tool still in flight, the dots hide automatically — so they can never get
   * stuck even if the bridge's terminal `cost.snapshot` is missing, late, or
   * followed by a stray delta. cost.snapshot is a fast-path instant hide, not the
   * sole mechanism. A hard cap guarantees the dots clear even if a tool never settles.
   */
  private typingIdleTimer: ReturnType<typeof setTimeout> | null = null;
  private typingShownAt = 0;
  /** Hide the dots after this much inactivity when no tool is in flight. */
  private static readonly TYPING_IDLE_MS = 2000;
  /** Absolute ceiling: hide the dots after this long regardless of tool state. */
  private static readonly TYPING_MAX_MS = 90000;
  /** Direct HTTP responses still pending for the active widget session. */
  private pendingDirectResponses = 0;
  private allowedModalities: WidgetModality[] = [];
  private readonly testerMode: boolean;
  private parentMsgListener: ((ev: MessageEvent) => void) | null = null;
  /** Window unload listener that ends the live session (reload / tab close). */
  private unloadListener: (() => void) | null = null;
  /** Guards the unload handler against firing more than once (pagehide + beforeunload). */
  private endBeaconSent = false;
  private sessionLocks: HumanChatSessionLock | null = null;
  private humanRestoreAttempt: object | null = null;
  /** Pending `behavior.openDelayMs` auto-open timer, cancelled if the visitor opens first. */
  private autoOpenTimer: ReturnType<typeof setTimeout> | null = null;
  /** toolCallId → toolName for interactive rich-content awaiting submission. */
  private pendingRich: Map<string, string> = new Map();
  /**
   * Trimmed user texts sent locally (and already rendered by handleSendText)
   * that are awaiting their bridge `transcript.turn` echo. Realtime agents
   * echo the caller's typed message back as a `customer` turn regardless of
   * modality; without this dedup the widget shows the message twice. Spoken
   * user turns never enter this list, so they still render normally.
   */
  private pendingUserEchoes: string[] = [];
  /**
   * Context values already relayed to the tester as `context.field.saved`.
   * Near-RT turns return the full context bag each turn; we diff against this
   * snapshot so only newly captured/changed fields are surfaced.
   */
  private nearRtRelayedContext: Record<string, string> = {};
  /** Context variables observed this session; sent when the server picks a post-chat survey. */
  private sessionContext: Record<string, string> = {};
  /** Survey shown after the conversation; no id = the default survey. */
  private activePostcall: { id?: string; form: WidgetPrecallForm } | null = null;

  constructor(private readonly opts: InitOptions) {
    this.api = new WidgetApi(opts.apiBaseUrl || resolveDefaultApiBase());
    this.testerMode = !!(opts.devTokenResponse && typeof window !== 'undefined' && window.parent && window.parent !== window);
    if (this.testerMode) this.installParentMessageListener();
    this.installUnloadHandler();
  }

  /** Origin to target for the admin-tester relay channel. */
  private get testerRelayOrigin(): string {
    // `window.location.origin` is unreliable inside the tester's `srcdoc`
    // iframe (`about:srcdoc` → "null"), so prefer the explicit origin handed
    // in by the tester host. Fall back to the document origin for safety.
    return this.opts.testerOrigin || (typeof window !== 'undefined' ? window.location.origin : '');
  }

  private relay(type: string, payload: Record<string, any> = {}): void {
    if (!this.testerMode || typeof window === 'undefined') return;
    try {
      // The tester preview always runs same-origin with the admin app, so we
      // target the app origin explicitly instead of '*' to avoid leaking
      // relayed session data to an unexpected parent window.
      window.parent.postMessage({ source: 'autoqm-widget', type, ...payload }, this.testerRelayOrigin);
    } catch { /* ignore */ }
  }

  /**
   * Relay a near-real-time turn to the admin tester. Near-RT agents are
   * request/response (no Realtime WS bridge), so they never flow through
   * `handlePubSubEvent` where realtime agents relay their stream. We
   * synthesise the equivalent tester events here so the Transcript / Tools /
   * Context tabs populate for near-RT agents too.
   */
  private relayNearRtTurn(userText: string | undefined, res: NearRealtimeTurnResponse, customerTimestamp?: string): void {
    this.rememberContext(res.contextValues);
    if (!this.testerMode) return;
    if (userText) {
      this.relay('pubsub.event', { event: { type: 'transcript.turn', turn: { role: 'customer', text: userText, timestamp: customerTimestamp || new Date().toISOString() } } });
    }
    // Diagnostic: surface the tool catalogue the agent had available this turn so
    // the tester shows what could have been called even when nothing fired.
    if (res.availableTools && res.availableTools.length) {
      this.relay('pubsub.event', { event: { type: 'tools.available', tools: res.availableTools } });
    }
    for (const call of res.toolCalls || []) {
      const toolCallId = `nrt-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const success = call.success !== false && (call.result == null || (call.result as any).success !== false);
      this.relay('pubsub.event', { event: { type: 'tool.call', toolCallId, name: call.name, arguments: call.arguments } });
      this.relay('pubsub.event', {
        event: {
          type: 'tool.result',
          toolCallId,
          name: call.name,
          success,
          dataPreview: this.previewToolResult(call.result),
          result: this.toolResultDetail(call.result),
          error: success ? undefined : ((call.result as any)?.error || undefined),
          status: success ? undefined : ((call.result as any)?.status || undefined),
        },
      });
    }
    // Surface newly captured context fields (ai_extracted / pre-call) as the
    // realtime path does, by diffing against what we've already relayed.
    const ctx = res.contextValues || {};
    for (const [field, value] of Object.entries(ctx)) {
      if (value == null || value === '') continue;
      if (this.nearRtRelayedContext[field] === value) continue;
      this.nearRtRelayedContext[field] = value;
      this.relay('pubsub.event', { event: { type: 'context.field.saved', field, value } });
    }
    // Per-AI-feature AI Credits ledger rows (chat / transcription / tts /
    // knowledge-search). Each becomes a row in the tester's Custo tab and an
    // Eventos log entry — the same figure that feeds the consumption reports.
    for (const ev of res.consumption?.events || []) {
      this.relay('pubsub.event', {
        event: {
          type: 'consumption.event',
          feature: ev.feature,
          operationType: ev.operationType,
          category: ev.category,
          turnIndex: ev.turnIndex,
          credits: ev.credits,
          totalTokens: ev.totalTokens,
          inputTokens: ev.inputTokens,
          outputTokens: ev.outputTokens,
          inputAudioTokens: ev.inputAudioTokens,
          outputAudioTokens: ev.outputAudioTokens,
          audioTokens: ev.audioTokens,
          cachedTextTokens: ev.cachedTextTokens,
          cachedAudioTokens: ev.cachedAudioTokens,
          cachedTokens: ev.cachedTokens,
          ttsUsage: ev.ttsUsage,
          sttUsage: ev.sttUsage,
          contextDrivers: ev.contextDrivers,
          confidence: ev.confidence,
          sessionCredits: res.consumption?.sessionCredits,
          timestamp: ev.timestamp,
        },
      });
    }
    // Surface engine errors / fallbacks so the tester sees why a turn degraded
    // instead of a silent hang.
    if (res.error || res.usedFallbackReply) {
      this.relay('pubsub.event', { event: { type: 'realtime.error', error: { message: res.error || 'fallback_reply', fallback: !!res.usedFallbackReply } } });
    }
    if (res.reply) {
      this.relay('pubsub.event', { event: { type: 'transcript.turn', turn: { role: 'agent', text: res.reply, timestamp: new Date().toISOString() } } });
    }
    if (res.handoff) {
      this.relay('pubsub.event', { event: { type: 'agent.handoff', handoff: res.handoff } });
    }
  }

  /**
   * Surface an agent-to-agent hand-off in the conversation: the dialogue
   * continues with another Virtual Agent in the same session. Shows a subtle
   * status note (no redirect) so the visitor sees who they're now talking to.
   */
  private handleNearRtHandoff(handoff?: { toAgentId: string; toAgentName?: string }): void {
    if (!handoff || !this.ui) return;
    const name = handoff.toAgentName || this.ui.tr('specialistAgent', 'a specialist');
    this.ui.setStatus(this.ui.tr('connectedToAgent', 'Connected to {name}').replace('{name}', name));
  }

  /** Compact, human-readable preview of a near-RT tool result for the tester. */
  private previewToolResult(result: any): string | undefined {
    if (result == null) return undefined;
    try {
      const s = typeof result === 'string' ? result : JSON.stringify(result);
      return s.length > 240 ? `${s.slice(0, 240)}…` : s;
    } catch {
      return undefined;
    }
  }

  /** Full (bounded) near-RT tool result so the tester can expand everything a tool returned. */
  private toolResultDetail(result: any): any {
    if (result == null) return undefined;
    try {
      const s = typeof result === 'string' ? result : JSON.stringify(result);
      if (s.length <= 50_000) return result;
      return `${s.slice(0, 50_000)}…`;
    } catch {
      return undefined;
    }
  }

  private installParentMessageListener(): void {
    if (typeof window === 'undefined') return;
    this.parentMsgListener = (ev: MessageEvent) => {
      // Only accept control messages from the admin tester host.
      if (ev.origin !== this.testerRelayOrigin) return;
      const data: any = ev.data;
      if (!data || typeof data !== 'object') return;
      if (data.source !== 'autoqm-tester') return;
      if (data.type === 'force.end') {
        this.handleEndConversation().catch(() => { /* */ });
      }
    };
    window.addEventListener('message', this.parentMsgListener);
  }

  /**
   * End the live Virtual Agent session when the page goes away (reload, tab
   * close, navigation). Near-real-time agents have NO persistent connection
   * whose closure could signal termination, so without this the interaction
   * record stays "InProgress" forever. `pagehide` is the reliable modern
   * signal (fires on reload/close and on bfcache eviction); `beforeunload` is
   * a fallback for older engines. A keepalive fetch (see `endSessionBeacon`)
   * carries the JWT and survives the unload.
   */
  private installUnloadHandler(): void {
    if (typeof window === 'undefined') return;
    const handler = (event: Event): void => {
      if (event.type === 'pagehide' && (event as PageTransitionEvent).persisted) return;
      const sess = this.session;
      if (!sess || this.humanMode || this.humanPreparing || this.endBeaconSent) return;
      this.endBeaconSent = true;
      this.api.endSessionBeacon(sess.token, {
        callId: sess.callId,
        nearRealtime: sess.nearRealtime,
        reason: 'page_unload',
      });
    };
    this.unloadListener = handler;
    window.addEventListener('pagehide', handler);
    window.addEventListener('beforeunload', handler);
  }

  private showBootstrapError(error: unknown, language?: string): void {
    const cfg: WidgetPublicConfig = {
      publicKey: this.opts.publicKey, name: '', status: 'draft',
      language: language || this.opts.language || document.documentElement.lang || 'en',
      theme: {}, branding: {}, precallForm: { enabled: false },
      files: { enabled: false }, modalities: { voice: false, text: true, default: 'text' },
    };
    this.ui = new WidgetUi(cfg);
    this.ui.on(event => {
      if (event.type === 'open') this.ui?.openUnavailable(error);
      else if (event.type === 'minimize' || event.type === 'end.session') this.ui?.renderLauncher();
    });
  }

  async init(): Promise<void> {
    let cfg: WidgetPublicConfig;
    // Admin tester path: use the pre-resolved publicConfig from
    // `issueWidgetDevToken` so DRAFT widgets render without calling the
    // public /getWidgetPublicConfig endpoint (which 410s on drafts).
    const devPublic = this.opts.devTokenResponse?.publicConfig;
    if (devPublic) {
      cfg = {
        ...devPublic,
        status: 'published',
        // Backend may omit optional sub-objects for widgets that never
        // configured a pre-call form / files / modalities. Default them
        // here so handleOpen() and the UI layer can read them safely.
        precallForm: devPublic.precallForm || { enabled: false, fields: [] },
        modalities: devPublic.modalities || { voice: false, text: true, defaultMode: 'text' } as any,
        files: devPublic.files || { enabled: false, maxFileMb: 10, allowedMimeTypes: [] } as any,
        // Keep the launcher visible inside the tester so the operator can
        // exercise minimize/restore exactly like an embedder will. Also do
        // NOT force autoOpen — the operator clicks the bubble themselves.
        behavior: { ...(devPublic.behavior || {}) },
      } as WidgetPublicConfig;
    } else {
      try {
        cfg = await this.api.getPublicConfig(this.opts.publicKey);
      } catch (err: any) {
        console.warn('[AutoQM widget] failed to load config:', err?.message || err);
        this.showBootstrapError(err);
        return;
      }
      if (cfg.status !== 'published') {
        console.warn('[AutoQM widget] config is not published; widget will not mount.');
        this.showBootstrapError({ code: 'not_published' }, cfg.language);
        return;
      }
    }
    this.cfg = cfg;
    this.ui = new WidgetUi(cfg, this.testerMode);
    // Replaying a recorded voice clip must silence any live streamed agent
    // audio so the clip and the live reply can never play simultaneously.
    this.ui.setOnClipPlay(() => {
      try { this.audio?.clearPlayback(); } catch { /* */ }
      // clearPlayback empties the queue without emitting `drained`, so mark it
      // here or the agent turn could never complete.
      this.playbackDrained = true;
      this.maybeFinishAgentTurn();
    });
    this.ui.setLinkPreviewResolver(async (url) => {
      const sess = this.session || this.lastSession;
      if (!sess) return null;
      return (await this.api.getLinkPreview(sess.token, url)).preview || null;
    });
    this.ui.on(async (e) => {
      try {
        if ((this.humanMode || this.humanPreparing) && !['open', 'minimize', 'end.session', 'chat.cancel', 'chat.retry', 'send.text', 'draft.changed', 'postcall.submit', 'postcall.skip'].includes(e.type)) return;
        if (e.type === 'open') this.handleOpen();
        else if (e.type === 'minimize') this.handleMinimize();
        else if (e.type === 'end.session') await this.handleEndConversation();
        else if (e.type === 'chat.cancel') await this.cancelHumanQueue();
        else if (e.type === 'chat.retry' && this.humanSendRetry) await this.handleSendText(this.humanSendRetry.text);
        else if (e.type === 'postcall.submit') await this.handlePostcallSubmit(e.data);
        else if (e.type === 'postcall.skip') this.handlePostcallSkip();
        else if (e.type === 'precall.submit') await this.handlePrecallSubmit(e.modality, e.data);
        else if (e.type === 'metadata.consent') await this.handleMetadataConsent(e.acceptedCategories);
        else if (e.type === 'send.text') await this.handleSendText(e.text);
        else if (e.type === 'draft.changed' && (this.humanMode || this.humanPreparing)) {
          this.humanDraft = e.text;
          this.persistHumanRecovery();
          this.updateHumanSendControls();
        }
        else if (e.type === 'mic.toggle') this.handleMicToggle();
        else if (e.type === 'mic.down') this.handleMicDown();
        else if (e.type === 'mic.up') this.handleMicUp();
        else if (e.type === 'file.select') await this.handleFileSelect(e.files);
        else if (e.type === 'file.remove') this.handleFileRemove(e.id);
        else if (e.type === 'rich.submit') await this.handleRichSubmit(e.toolCallId, e.formData);
        else if (e.type === 'feedback.submit') await this.handleFeedbackSubmit(e);
        else if (e.type === 'pill.click') await this.handlePillClick(e.pill, e.parameters);
        else if (e.type === 'agent.stop') this.stopAgentPlayback();
      } catch (err: any) {
        console.warn('[AutoQM widget] ui handler failed:', err?.message || err);
        this.ui?.setError(this.ui.errorMessage(err));
      }
    });
    if (await this.restoreHumanChat()) return;
    if (cfg.behavior?.autoOpen) {
      const delay = Math.max(0, Number(cfg.behavior.openDelayMs) || 0);
      if (delay > 0) {
        this.autoOpenTimer = setTimeout(() => { this.autoOpenTimer = null; this.handleOpen(); }, delay);
      } else {
        this.handleOpen();
      }
    }
  }

  private handleOpen(): void {
    if (!this.ui || !this.cfg) return;
    if (this.autoOpenTimer) { clearTimeout(this.autoOpenTimer); this.autoOpenTimer = null; }
    // Live session — the user is restoring the panel from minimized state.
    if (this.session || this.humanPreparing) {
      this.ui.restorePanel();
      if (this.pendingDirectResponses > 0) this.ui.showTyping();
      this.relay('panel.restored');
      return;
    }
    const pf = this.cfg.precallForm;
    const needsPrecall = !!(pf?.enabled && pf.fields && pf.fields.length);
    const needsMetadataConsent = !!(this.cfg.metadata?.enabled && this.cfg.metadata.fields?.some(field => field.enabled !== false));
    this.ui.openPanel(needsPrecall, needsMetadataConsent);
    if (!needsPrecall && !needsMetadataConsent) {
      // Auto-start with default modality. Backend serializes the default as
      // `defaultMode`; older builds of this SDK looked for `.default` which
      // always missed and silently downgraded voice widgets to text.
      const mods: any = this.cfg.modalities || {};
      const defaultModality: WidgetModality = mods.defaultMode || mods.default || 'text';
      this.startSession(defaultModality, {}).catch((err) => {
        this.ui?.setError(this.ui.errorMessage(err));
      });
    }
  }

  private widgetCapturedMetadata: Record<string, any> = {};
  private widgetConsentEvidence: Record<string, any> | undefined;

  private async handleMetadataConsent(acceptedCategories: string[]): Promise<void> {
    if (!this.cfg || !this.ui) return;
    const accepted = new Set(acceptedCategories);
    this.widgetCapturedMetadata = await this.collectWidgetMetadata(accepted);
    this.widgetConsentEvidence = {
      acceptedCategories: Array.from(accepted),
      declinedCategories: ['personalization', 'analytics', 'precise_location'].filter(category => !accepted.has(category)),
      timestamp: new Date().toISOString(),
      noticeVersion: this.cfg.version || 1,
      locale: this.cfg.language || navigator.language || 'en',
    };
    const fields = this.cfg.precallForm?.fields || [];
    const needsPrecall = !!(this.cfg.precallForm?.enabled && fields.length);
    this.ui.continueAfterMetadataConsent(needsPrecall);
    if (!needsPrecall) {
      const modalities: any = this.cfg.modalities || {};
      await this.startSession(modalities.defaultMode || modalities.default || 'text', {});
    }
  }

  private async collectWidgetMetadata(accepted: Set<string>): Promise<Record<string, any>> {
    const result: Record<string, any> = {};
    const cleanUrl = (raw: string): string => {
      try { const url = new URL(raw); return `${url.origin}${url.pathname}`.slice(0, 1024); } catch { return ''; }
    };
    const isMobile = matchMedia('(pointer: coarse)').matches || /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent);
    for (const field of this.cfg?.metadata?.fields || []) {
      if (field.enabled === false || !accepted.has(field.category)) continue;
      let value = '';
      if (field.fieldId === 'page_url') value = cleanUrl(location.href);
      else if (field.fieldId === 'page_title') value = document.title.slice(0, 300);
      else if (field.fieldId === 'referrer_url') value = cleanUrl(document.referrer);
      else if (field.fieldId === 'browser_language') value = navigator.language || '';
      else if (field.fieldId === 'timezone') value = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      else if (field.fieldId === 'viewport') value = `${window.innerWidth}x${window.innerHeight}`;
      else if (field.fieldId === 'screen') value = `${screen.width}x${screen.height}`;
      else if (field.fieldId === 'device_class') value = isMobile ? 'mobile' : 'desktop';
      else if (field.fieldId === 'is_mobile') value = String(isMobile);
      else if (field.fieldId === 'sdk_version') value = '1';
      else if (field.fieldId === 'precise_location') value = await new Promise<string>(resolve => {
        if (!navigator.geolocation) return resolve('');
        navigator.geolocation.getCurrentPosition(
          position => resolve(`${position.coords.latitude.toFixed(6)},${position.coords.longitude.toFixed(6)};accuracy=${Math.round(position.coords.accuracy)}`),
          () => resolve(''),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
        );
      });
      if (!value) continue;
      result[field.contextVariable] = {
        value: value.replace(/[\u0000-\u001F\u007F]/g, ' ').trim().slice(0, 1024),
        source: 'widget',
        sourceKey: field.fieldId,
        category: field.category,
        label: field.label,
        agentVisible: field.injectIntoAgent === true,
      };
    }
    return result;
  }

  /** Hide panel but keep session, transport, audio, and transcript alive. */
  private handleMinimize(): void {
    if (!this.ui) return;
    this.ui.minimizePanel();
    this.ui.setSessionActive(!!this.session);
  }

  private isCurrentSession(sess: { sid: string; callId: string }): boolean {
    return !this.humanMode && this.session?.sid === sess.sid && this.session.callId === sess.callId;
  }

  private beginDirectResponse(sess: { sid: string; callId: string }): () => void {
    if (!this.isCurrentSession(sess)) return () => {};
    this.pendingDirectResponses += 1;
    this.ui?.showTyping();
    let finished = false;
    return () => {
      if (finished) return;
      finished = true;
      if (!this.isCurrentSession(sess)) return;
      this.pendingDirectResponses = Math.max(0, this.pendingDirectResponses - 1);
      if (this.pendingDirectResponses === 0) this.ui?.hideTyping();
      else this.ui?.showTyping();
    };
  }

  /** Explicit teardown + optional post-call survey. */
  private async cancelHumanQueue(): Promise<void> {
    if (!this.humanChat || this.humanCancelBusy || !this.canCancelHumanQueue()) return;
    const channel = this.humanChat;
    this.humanCancelBusy = true;
    this.ui?.setHumanQueueCancel(humanChatLabel(this.cfg?.language, 'cancel'), true, true);
    try {
      this.humanCancelCommand ||= crypto.randomUUID();
      await channel.command('customer-cancel', this.humanCancelCommand);
      if (this.humanChat === channel) this.ui?.clearError();
    } catch {
      if (this.humanChat === channel) this.ui?.setError(humanChatLabel(this.cfg?.language, 'unavailable'));
    } finally {
      if (this.humanChat === channel) {
        this.humanCancelBusy = false;
        this.ui?.setHumanQueueCancel(humanChatLabel(this.cfg?.language, 'cancel'), this.canCancelHumanQueue());
      }
    }
  }

  private async handleEndConversation(): Promise<void> {
    if (this.humanEndBusy) return;
    if (this.humanPreparing && !this.session) {
      await this.teardownSession('user_ended');
      this.ui?.setSessionActive(false);
      this.ui?.renderLauncher();
      return;
    }
    if (this.humanChat && !['closed', 'wrap_up'].includes(this.humanState?.status || '')) {
      const session = this.session;
      const channel = this.humanChat;
      this.humanEndBusy = true;
      try {
        this.humanEndCommand ||= crypto.randomUUID();
        await channel.command('customer-end', this.humanEndCommand);
        if (this.session !== session || this.humanChat !== channel) return;
      } catch {
        if (this.session === session && this.humanChat === channel) this.ui?.setError(humanChatLabel(this.cfg?.language, 'unavailable'));
        return;
      } finally {
        if (this.session === session && this.humanChat === channel) this.humanEndBusy = false;
      }
    }
    await this.concludeConversation('user_ended');
  }

  /**
   * Single exit path for a conversation, whatever ended it (visitor button,
   * the agent's end-call tool, or the bridge closing). Tears the session down
   * and then shows the post-chat survey.
   */
  private async concludeConversation(reason: string): Promise<void> {
    if (this.humanMode && reason !== 'user_ended') return;
    if (this.concluding || this.concluded || !this.ui) return;
    this.concluding = true;
    this.concluded = true;
    try {
      if (reason !== 'user_ended') await this.waitForAgentPlayback();
      this.ui.setSessionActive(false);
      // A minimized panel must not be forced back open just to survey.
      const postcall = this.ui.isPanelVisible() ? await this.pickPostcallSurvey(reason) : null;
      this.activePostcall = postcall;
      if (postcall) this.ui.renderPostcall(postcall.form);
      else this.ui.renderLauncher();
      // Teardown and the transcript hand-off are network round-trips; running
      // them behind the survey keeps the visitor from staring at a dead panel.
      const closing = this.session ? this.teardownSession(reason) : Promise.resolve();
      void closing
        .catch(() => { /* */ })
        .then(() => this.flushTranscriptRequests())
        .catch(() => { /* */ });
    } finally {
      this.concluding = false;
    }
  }

  private rememberContext(values: Record<string, unknown> | undefined): void {
    for (const [key, value] of Object.entries(values || {})) {
      if (value != null && value !== '') this.sessionContext[key] = String(value);
    }
  }

  /**
   * Default survey unless several surveys exist (IVA-level surveys always list
   * the default too), in which case the server evaluates their private
   * conditions and delivery rules. Falls back to the default on failure.
   */
  private async pickPostcallSurvey(reason: string): Promise<{ id?: string; form: WidgetPrecallForm } | null> {
    const showable = (f?: WidgetPrecallForm): f is WidgetPrecallForm => !!(f?.enabled && f.fields?.length);
    const defaultForm = this.cfg?.postcallForm;
    const fallback = showable(defaultForm) ? { form: defaultForm } : null;
    const conditional = (this.cfg?.postcallSurveys || []).filter(showable);
    const sess = this.lastSession;
    if (!conditional.length || !sess) return fallback;
    const customerTurns = (this.ui?.getTranscript() || []).filter(entry => entry.role === 'customer').length;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const res = await Promise.race([
        this.api.resolvePostcallSurvey(sess.token, { callId: sess.callId, context: this.sessionContext, reason, customerTurns }),
        new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error('timeout')), 5000); }),
      ]);
      if (!res?.surveyId) return null;
      const match = conditional.find(s => s.id === res.surveyId);
      return match ? { id: match.id, form: match } : fallback;
    } catch (err: any) {
      console.warn('[AutoQM widget] postcall survey resolution failed:', err?.message || err);
      return fallback;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  /**
   * Lets the agent's closing line finish before the panel switches away. Only
   * applies when the agent/bridge ended the call — an explicit visitor "end"
   * should take effect immediately.
   */
  private async waitForAgentPlayback(maxWaitMs = 20000): Promise<void> {
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      if (!this.agentAudioActive && !this.urlAudio) return;
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  /** Queues the transcript-email answers of a submitted survey. */
  private queueTranscriptRequests(
    fields: Array<{ id: string; type?: string; contextKey?: string }> | undefined,
    data: Record<string, string>,
    source: 'pre' | 'post',
  ): void {
    for (const f of fields || []) {
      if (f.type !== 'transcript_email') continue;
      const email = data[f.contextKey || f.id];
      if (email) this.pendingTranscriptRequests.push({ fieldId: f.id, email, source });
    }
  }

  /** Best-effort delivery of queued transcript requests once a session exists. */
  private async flushTranscriptRequests(): Promise<void> {
    if (!this.pendingTranscriptRequests.length) return;
    const sess = this.lastSession;
    if (!sess) { this.pendingTranscriptRequests = []; return; }
    const queued = this.pendingTranscriptRequests;
    this.pendingTranscriptRequests = [];
    const fallbackTranscript = this.ui?.getTranscript() || [];
    for (const req of queued) {
      try {
        await this.api.requestTranscript(sess.token, { callId: sess.callId, ...req, fallbackTranscript });
        this.relay('transcript.requested', { fieldId: req.fieldId, source: req.source });
      } catch (err: any) {
        console.warn('[AutoQM widget] transcript request failed:', err?.message || err);
      }
    }
  }

  private async handleFeedbackSubmit(e: {
    messageId: string; answer: string; question?: string; rating: 'up' | 'down'; comment?: string;
  }): Promise<void> {
    const sess = this.session || this.lastSession;
    if (!sess) return;
    try {
      await this.api.submitFeedback(sess.token, {
        callId: sess.callId,
        messageId: e.messageId,
        rating: e.rating,
        comment: e.comment,
        question: e.question,
        answer: e.answer,
      });
      this.relay('feedback.submitted', { messageId: e.messageId, rating: e.rating, hasComment: !!e.comment });
    } catch (err: any) {
      console.warn('[AutoQM widget] feedback submit failed:', err?.message || err);
    }
  }

  private async handlePostcallSubmit(data: Record<string, string>): Promise<void> {
    if (!this.ui) return;
    const sess = this.lastSession;
    if (sess) {
      try {
        await this.api.submitPostcallForm(sess.token, { callId: sess.callId, data, surveyId: this.activePostcall?.id });
        this.relay('postcall.submitted', { data, surveyId: this.activePostcall?.id });
      } catch (err: any) {
        console.warn('[AutoQM widget] postcall submit failed:', err?.message || err);
      }
    }
    this.queueTranscriptRequests(this.activePostcall?.form.fields as any, data, 'post');
    void this.flushTranscriptRequests().catch(() => { /* */ });
    this.ui.renderThankYou();
  }

  private handlePostcallSkip(): void {
    this.relay('postcall.skipped');
    this.ui?.renderLauncher();
  }

  private async handlePrecallSubmit(modality: WidgetModality, data: Record<string, string>): Promise<void> {
    if (!this.cfg || !this.ui) return;
    this.queueTranscriptRequests(this.cfg.precallForm?.fields as any, data, 'pre');
    this.ui.renderConversation();
    await this.startSession(modality, data);
  }

  private async handleSendText(text: string): Promise<void> {
    if (this.humanPreparing) return;
    if (!this.session || !this.ui) return;
    if (this.humanMode) {
      if (!this.humanChat || this.humanSending || !text.trim()) return;
      const session = this.session;
      const channel = this.humanChat;
      this.humanSending = true;
      this.updateHumanSendControls();
      try {
        await channel.send(text);
        if (this.session !== session || this.humanChat !== channel) return;
        this.ui?.clearError();
      } catch {
        if (this.session === session && this.humanChat === channel) this.ui?.setError(humanChatLabel(this.cfg?.language, 'unavailable'));
      } finally {
        if (this.session === session && this.humanChat === channel) {
          this.humanSending = false;
          this.updateHumanSendControls();
        }
      }
      return;
    }
    // A typed message supersedes whatever chips were on offer.
    this.ui.clearPills();
    // Realtime text: finalize any still-open agent partial from the previous
    // turn BEFORE rendering this user message, so the new user bubble appears
    // below the agent reply (a reused/un-finalized partial would otherwise be
    // repositioned above it). Discard a stray fragment, commit a real partial.
    if (!this.session.nearRealtime && this.ui.hasPartial('agent')) {
      if (this.committedAgentThisTurn) this.ui.discardPartial('agent');
      else this.ui.commitPartial('agent', this.partialAgent);
      this.partialAgent = '';
      this.committedAgentThisTurn = false;
    }
    if (!this.session.nearRealtime) this.ui.sweepStrayPartials();
    // Attach any ready files to this outgoing message, then clear the composer's
    // files row + pending set. The file content was already injected into the
    // agent's context at upload time, so we only surface it in the transcript.
    const pendingFiles = this.pendingFiles.splice(0);
    const attachments = pendingFiles.map(f => ({ name: f.name, mimeType: f.mimeType, size: f.size }));
    const fileIds = pendingFiles.map(f => f.id);
    if (attachments.length) {
      this.ui.clearFileChips();
      this.syncComposerFilesState();
    }
    this.ui.appendMessage({
      id: `u-${Date.now()}`,
      role: 'user',
      text,
      ts: new Date().toISOString(),
      attachments: attachments.length ? attachments : undefined,
    });
    // When the visitor sends attachments without typing, give the agent a
    // concrete turn referencing the file(s) instead of an empty message.
    const outgoingText = text || (attachments.length
      ? `[Attached ${attachments.length === 1 ? 'file' : 'files'}: ${attachments.map(a => a.name).join(', ')}]`
      : text);
    // A new user turn: stop dropping agent audio so the upcoming reply plays.
    this.droppedResponseId = null;
    // Near-real-time: plain request/response — render the reply inline.
    if (this.session.nearRealtime) {
      const sess = this.session;
      const customerTimestamp = new Date().toISOString();
      this.ui.setStatus(this.ui.tr('thinking', 'Thinking…'));
      const finishDirectResponse = this.beginDirectResponse(sess);
      const stopWorkingFiller = this.beginNearRealtimeWorkingFiller();
      try {
        const res = await this.api.nearRealtimeTurn(sess.token, {
          message: outgoingText,
          fileIds: fileIds.length ? fileIds : undefined,
        });
        if (!this.isCurrentSession(sess)) return;
        if (res.reply) {
          this.ui.appendMessage({
            id: `a-${Date.now()}`,
            role: 'agent',
            text: res.reply,
            ts: new Date().toISOString(),
            audioUrl: res.audioUrl,
          });
        }
        this.relayNearRtTurn(res.userTranscript || outgoingText, res, customerTimestamp);
        this.renderNearRtDirectives(res.renderDirectives);
        this.ui.renderPills(res.pills || []);
        if (res.audioUrl) this.playUrlAudio(res.audioUrl);
        this.ui.setStatus(this.cfg?.branding.greeting || this.ui.tr('connected', 'Connected'));
        if (res.handoff) this.handleNearRtHandoff(res.handoff);
        if (res.transferred && res.transfer) this.handleTransferDirective(res.transfer);
        if (res.ended) {
          this.ui.setStatus(this.ui.tr('conversationEnded', 'Conversation ended'));
          this.concludeConversation('agent_ended').catch(() => { /* */ });
        }
      } catch (err: any) {
        if (this.isCurrentSession(sess)) this.handleNearRtTurnError(err);
      } finally {
        stopWorkingFiller();
        finishDirectResponse();
      }
      return;
    }
    // Remember this turn BEFORE sending so the bridge's echoed `transcript.turn`
    // (the realtime backend emits one for every caller message, and it can
    // arrive while the ingest POST below is still in flight) is reliably
    // suppressed instead of double-rendering as a second user bubble.
    this.pendingUserEchoes.push(outgoingText.trim());
    const sess = this.session;
    await this.api.ingest(sess.token, {
      callId: sess.callId,
      type: 'text',
      text: outgoingText,
    });
    // Realtime text: the agent is now working. Show the typing dots and arm the
    // self-healing watchdog (see bumpTyping). Reset the in-flight set for the
    // new turn so a previous turn's leaked id can't keep the dots alive.
    this.agentBusy = true;
    this.toolsInFlightIds.clear();
    this.bumpTyping();
  }

  /** Render near-RT inline rich-content directives and track interactive ones. */
  private renderNearRtDirectives(directives?: NearRealtimeTurnResponse['renderDirectives']): void {
    if (!directives || !directives.length || !this.ui) return;
    for (const d of directives) {
      if (d.interaction === 'await_submit') this.pendingRich.set(d.toolCallId, d.toolName);
      this.ui.appendRichContent(d as any);
    }
  }

  /**
   * A quick-reply chip was tapped. Text chips become the next user turn,
   * deeplinks open a window, and tool chips run through the allow-listed
   * backend endpoint (the visitor can never name a tool itself).
   */
  private async handlePillClick(pill: WidgetPill, parameters?: Record<string, any>): Promise<void> {
    const sess = this.session || this.lastSession;
    if (!this.ui || !sess) return;
    this.relay('pill.clicked', { pillId: pill.id, kind: pill.kind, label: pill.label });

    if (pill.kind === 'deeplink') {
      // Re-check client-side: the server allow-lists schemes, but never trust a
      // single gate before handing a URL to the browser.
      if (!isSafePillUrl(pill.url)) return;
      // Open first — browsers only honour a popup inside the click's gesture,
      // so the alert must not be awaited before it.
      openExternal(pill.url!, pill.target);
      if (pill.notify) {
        void this.api.notifyPillAlert(sess.token, {
          callId: sess.callId,
          pillId: pill.id,
          fallbackTranscript: this.ui.getTranscript(),
        }).catch((err: any) => console.warn('[AutoQM widget] pill alert failed:', err?.message || err));
      }
      return;
    }

    if (pill.kind === 'tool') {
      if (!this.isCurrentSession(sess)) return;
      this.ui.appendMessage({ id: `u-${Date.now()}`, role: 'user', text: pill.label, ts: new Date().toISOString() });
      const finishDirectResponse = this.beginDirectResponse(sess);
      try {
        const res = await this.api.invokePill(sess.token, { callId: sess.callId, pillId: pill.id, parameters });
        if (!this.isCurrentSession(sess)) return;
        if (res.text) {
          this.ui.appendMessage({ id: `a-${Date.now()}`, role: 'agent', text: res.text, ts: new Date().toISOString() });
        }
        this.renderNearRtDirectives(res.renderDirectives as any);
      } catch (err: any) {
        if (this.isCurrentSession(sess)) {
          console.warn('[AutoQM widget] pill tool failed:', err?.message || err);
          this.ui.setError(this.ui.tr('connectionError', 'Connection error'));
        }
      } finally {
        finishDirectResponse();
      }
      return;
    }

    await this.handleSendText(pill.text || pill.label);
  }

  /** A user submitted an interactive rich-content form. */
  private async handleRichSubmit(toolCallId: string, formData: Record<string, any>): Promise<void> {
    const sess = this.session || this.lastSession;
    if (!sess || !this.ui) return;
    const toolName = this.pendingRich.get(toolCallId);
    this.pendingRich.delete(toolCallId);
    let stopWorkingFiller = () => {};
    try {
      if (sess.nearRealtime) {
        if (!toolName) return;
        this.ui.setStatus(this.ui.tr('thinking', 'Thinking…'));
        const finishDirectResponse = this.beginDirectResponse(sess);
        stopWorkingFiller = this.beginNearRealtimeWorkingFiller();
        try {
          const res = await this.api.nearRealtimeTurn(sess.token, { richContentSubmit: { toolName, formData } });
          if (!this.isCurrentSession(sess)) return;
          if (res.reply) {
            this.ui.appendMessage({ id: `a-${Date.now()}`, role: 'agent', text: res.reply, ts: new Date().toISOString(), audioUrl: res.audioUrl });
          }
          this.relayNearRtTurn(undefined, res);
          this.renderNearRtDirectives(res.renderDirectives);
          if (res.audioUrl) this.playUrlAudio(res.audioUrl);
          this.ui.setStatus(this.cfg?.branding.greeting || this.ui.tr('connected', 'Connected'));
          if (res.handoff) this.handleNearRtHandoff(res.handoff);
          if (res.transferred && res.transfer) this.handleTransferDirective(res.transfer);
          if (res.ended) {
            this.ui.setStatus(this.ui.tr('conversationEnded', 'Conversation ended'));
            this.concludeConversation('agent_ended').catch(() => { /* */ });
          }
        } finally {
          stopWorkingFiller();
          finishDirectResponse();
        }
      } else {
        // Realtime bridge: resolve the deferred tool call via ingest.
        await this.api.ingest(sess.token, {
          callId: sess.callId,
          type: 'richContentSubmit',
          toolCallId,
          formData,
        });
      }
    } catch (err: any) {
      if (this.isCurrentSession(sess)) this.ui.setError(this.ui.errorMessage(err));
    }
  }

  /** Play a remote audio URL (near-RT synthesised speech / greeting). */
  private playUrlAudio(url: string, onEnded?: () => void, filler = false): void {
    if (!filler && this.workingFillerAudioActive && this.workingFillerIsSpeech) {
      this.pendingAfterFiller.push(() => this.playUrlAudio(url, onEnded));
      return;
    }
    // Stop any clip still playing (e.g. the greeting, or a previous turn's
    // reply) so back-to-back utterances don't overlap — near-RT spawns a fresh
    // element per turn and there is no shared playback queue to serialise them.
    this.stopUrlAudio();
    this.workingFillerAudioActive = filler;
    try {
      const el = new Audio(url);
      this.urlAudio = el;
      this.localWorkingFiller?.audioStarted();
      this.reportPlayback(true);
      if (filler) this.onAgentAudioActivity();
      // Near-RT voice: show the icon-only ■ STOP button on the newest agent
      // bubble so the visitor can stop the reply mid-playback.
      this.ui?.showAgentStop();
      this.ui?.setAvatarSpeaking(true);
      this.startUrlAudioMeter(el);
      const finish = (): void => {
        if (this.urlAudio !== el) return;
        this.urlAudio = null;
        this.workingFillerAudioActive = false;
        if (this.fillerFinishTimer) clearTimeout(this.fillerFinishTimer);
        this.fillerFinishTimer = null;
        this.localWorkingFiller?.audioEnded();
        this.reportPlayback(false);
        this.ui?.hideAgentStop();
        this.stopUrlAudioMeter();
        this.ui?.setAvatarSpeaking(false);
        try { onEnded?.(); } catch { /* */ }
        const queued = this.pendingAfterFiller.splice(0);
        for (const play of queued) play();
        if (!this.urlAudio && !this.agentAudioActive) {
          this.playbackDrained = true;
          this.armMicRestoreWatchdog();
        }
      };
      el.addEventListener('ended', finish, { once: true });
      el.addEventListener('error', finish, { once: true });
      if (filler) this.fillerFinishTimer = setTimeout(() => { el.pause(); finish(); }, 6000);
      el.play().catch(finish);
    } catch {
      this.ui?.hideAgentStop();
      this.ui?.setAvatarSpeaking(false);
      try { onEnded?.(); } catch { /* */ }
    }
  }

  private beginNearRealtimeWorkingFiller(): () => void {
    const filler = this.nearRealtimeWorkingFiller;
    if (!filler || this.session?.modality !== 'voice') return () => {};
    if (!this.localWorkingFiller) {
      this.localWorkingFiller = createWorkingFillerController({
        primaryLanguage: this.cfg?.language,
        aiSettings: { fillerMode: filler.mode, fillerDelayMs: filler.delayMs, fillerCooldownMs: filler.cooldownMs },
      } as any, {
        canSpeak: () => !!this.session && !this.urlAudio && !this.agentAudioActive && !this.micHeld,
        speak: phrase => {
          this.workingFillerIsSpeech = filler.mode === 'vocal_filler';
          this.relay('pubsub.event', { event: { type: 'working.started', mode: filler.mode, source: 'near-realtime-local' } });
          const delayed = phrase === resolveWorkingDelayPhrase({ primaryLanguage: this.cfg?.language } as any);
          this.playUrlAudio(delayed ? filler.delayedAudioUrl || filler.audioUrl : filler.audioUrl, undefined, true);
        },
      }, undefined, { enabled: true });
    }
    this.localWorkingFiller.startTurn();
    return () => {
      this.localWorkingFiller?.endTurn();
      if (this.workingFillerAudioActive && !this.workingFillerIsSpeech) this.stopUrlAudio();
      this.relay('pubsub.event', { event: { type: 'working.stopped', source: 'near-realtime-local' } });
    };
  }

  private reportPlayback(active: boolean): void {
    const session = this.session;
    if (!session || session.nearRealtime || session.modality !== 'voice') return;
    const sequence = ++this.playbackSequence;
    this.enqueueAudioSend(() => this.api.ingest(session.token, {
      callId: session.callId, type: 'playback', active, sequence,
    }));
  }

  /**
   * Feed the avatar from a played <audio> clip. Routing CORS-cross-origin media
   * (e.g. blob-storage filler clips) through WebAudio outputs silence, so those
   * clips play directly and the state change alone carries the reaction.
   */
  private startUrlAudioMeter(el: HTMLAudioElement): void {
    this.stopUrlAudioMeter();
    try {
      const src = new URL(el.src, window.location.href);
      if (src.protocol !== 'blob:' && src.protocol !== 'data:' && src.origin !== window.location.origin) return;
      const Ctor: typeof AudioContext = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctor) return;
      if (!this.urlAudioCtx) this.urlAudioCtx = new Ctor();
      const ctx = this.urlAudioCtx;
      // Routing the element through WebAudio silences it while the context is
      // suspended, so the meter is skipped until a later clip finds it running.
      if (ctx.state !== 'running') { void ctx.resume?.().catch(() => { /* */ }); return; }
      const source = ctx.createMediaElementSource(el);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      const buffer = new Uint8Array(analyser.frequencyBinCount);
      const tick = (): void => {
        if (this.urlAudio !== el) return;
        analyser.getByteTimeDomainData(buffer);
        let sum = 0;
        for (let i = 0; i < buffer.length; i++) {
          const v = (buffer[i] - 128) / 128;
          sum += v * v;
        }
        this.ui?.setVoiceAvatarLevel(Math.min(1, Math.sqrt(sum / buffer.length) * 3.2));
        this.urlAudioRaf = requestAnimationFrame(tick);
      };
      this.urlAudioRaf = requestAnimationFrame(tick);
    } catch { /* analyser is best-effort — playback must never depend on it */ }
  }

  private stopUrlAudioMeter(): void {
    if (this.urlAudioRaf) { cancelAnimationFrame(this.urlAudioRaf); this.urlAudioRaf = 0; }
    this.ui?.setVoiceAvatarLevel(0);
  }

  /** Stop and release the current near-RT / greeting URL-audio clip, if any. */
  private stopUrlAudio(): void {
    const el = this.urlAudio;
    this.urlAudio = null;
    this.pendingAfterFiller = [];
    this.workingFillerAudioActive = false;
    if (this.fillerFinishTimer) clearTimeout(this.fillerFinishTimer);
    this.fillerFinishTimer = null;
    this.ui?.hideAgentStop();
    this.stopUrlAudioMeter();
    this.ui?.setAvatarSpeaking(false);
    if (!el) return;
    this.localWorkingFiller?.audioEnded();
    this.reportPlayback(false);
    try { el.pause(); el.currentTime = 0; el.src = ''; } catch { /* */ }
  }

  /**
   * Park the avatar at rest — but never while the agent is mid-utterance.
   * `bridge.ready` lands a beat after the greeting's first audio chunks, and an
   * unconditional reset there froze the greeting's reaction after one pulse.
   */
  private resetAvatarIdle(): void {
    if (this.agentAudioActive || this.urlAudio) return;
    this.ui?.setVoiceAvatarState('idle');
  }

  private handleMicToggle(): void {
    if (!this.ui) return;
    // Near-RT voice: the mic is a push-to-talk button (record → submit),
    // since there is no continuous Realtime stream to mute.
    if (this.session?.nearRealtime) {
      this.handleNearRealtimePushToTalk().catch((err: any) => {
        this.ui?.setMicRecording(false);
        this.ui?.setError(widgetErrorMessage(err, this.cfg?.language, 'microphone'));
      });
      return;
    }
    // Realtime hands-free: server VAD owns turn boundaries, so a tap is only a
    // MUTE toggle — it never commits. The visitor just talks.
    if (!this.audio) return;
    const sess = this.session;
    if (!sess) return;
    // Agent verbalizing → a tap BARGES IN: stop the agent (local + server) and
    // re-open the mic so the visitor can talk immediately.
    if (this.agentSpeaking) {
      this.interruptAgent();
      this.capturedThisTurn = false;
      // Start a fresh voice-clip capture for the interrupting turn.
      this.userClipChunks = [];
      this.userMicMuted = false;
      this.audio.setMuted(false);
      this.ui.setMicMuted(false);
      this.ui.setStatus(this.ui.tr('listening', 'Listening…'));
      return;
    }
    if (this.audio.isMuted()) {
      // UNMUTE: resume streaming; server VAD picks the turn up on its own.
      this.capturedThisTurn = false;
      this.userClipChunks = [];
      this.userMicMuted = false;
      this.audio.setMuted(false);
      this.ui.setMicMuted(false);
      this.ui.setStatus(this.ui.tr('listening', 'Listening…'));
      return;
    }
    // MUTE: flush trailing speech while the mic is still live (those frames
    // must pass the onFrame gate) so a half-spoken sentence still reaches the
    // server, then stop capturing. No commit — server VAD closes the turn.
    void (async () => {
      try { await this.audio?.flushPending(); } catch { /* */ }
      this.userMicMuted = true;
      this.audio?.setMuted(true);
      this.ui?.setMicMuted(true);
      this.ui?.setMicLevel(0);
      this.userClipChunks = [];
      this.ui?.setStatus(this.ui.tr('connected', 'Connected'));
    })();
  }

  /** Append an ordered realtime-voice send (audio frame or commit). */
  private enqueueAudioSend(fn: () => Promise<unknown>): void {
    const send = () => this.humanMode ? Promise.resolve() : fn();
    this.audioSendChain = this.audioSendChain.then(send, send).catch(() => { /* network blips swallowed */ });
  }

  /**
   * Continuous open-mic: the agent started verbalizing. Auto-mute the live mic
   * so the agent's own playback can't re-enter it and trigger a phantom user
   * turn (echo loop), and reflect the "agent speaking — tap to interrupt" state.
   */
  private onAgentAudioActivity(): void {
    const sess = this.session;
    if (!sess || sess.nearRealtime || sess.modality !== 'voice' || sess.captureMode !== 'continuous') return;
    // Fresh audio queued → a premature drain can't end the turn yet.
    this.clearMicRestoreTimer();
    this.playbackDrained = false;
    if (!this.agentSpeaking) {
      this.agentSpeaking = true;
      this.agentResponseDone = false;
      this.audio?.setMuted(true);
      this.ui?.setAgentSpeaking(true);
    }
  }

  /**
   * Continuous open-mic: end the agent's speaking turn only once BOTH the server
   * reported the response done AND local playback has fully drained, then restore
   * the visitor's intended mic state. Requiring both avoids flapping between
   * audio deltas (brief queue gaps) and premature un-muting before playback ends.
   */
  private maybeFinishAgentTurn(): void {
    const sess = this.session;
    if (!sess || sess.nearRealtime || sess.modality !== 'voice' || sess.captureMode !== 'continuous') return;
    if (!this.agentSpeaking || !this.agentResponseDone || !this.playbackDrained) return;
    this.clearMicRestoreTimer();
    this.agentSpeaking = false;
    this.ui?.setAgentSpeaking(false);
    this.audio?.setMuted(this.userMicMuted);
    this.ui?.setMicMuted(this.userMicMuted);
  }

  private clearMicRestoreTimer(): void {
    if (this.micRestoreTimer === null) return;
    clearTimeout(this.micRestoreTimer);
    this.micRestoreTimer = null;
  }

  /**
   * Recovery net for the hands-free mic. It is normally restored only once the
   * server also reports the turn finished, but that event can never arrive — a
   * forced continuation that never materializes, or a dropped cost.snapshot —
   * which would strand the visitor muted. Once playback has been silent for a
   * beat, end the turn locally instead. This only ever un-mutes; any further
   * agent audio re-mutes through onAgentAudioActivity.
   */
  private armMicRestoreWatchdog(): void {
    const sess = this.session;
    if (!sess || sess.nearRealtime || sess.modality !== 'voice' || sess.captureMode !== 'continuous') return;
    this.clearMicRestoreTimer();
    this.micRestoreTimer = window.setTimeout(() => {
      this.micRestoreTimer = null;
      if (!this.agentSpeaking || !this.playbackDrained) return;
      this.agentResponseDone = true;
      this.maybeFinishAgentTurn();
    }, MIC_RESTORE_GRACE_MS);
  }

  /**
   * Realtime voice: build the current response's replayable voice clip from its
   * accumulated PCM and attach it to the agent bubble. Playback itself streams
   * each delta directly into the worklet (low latency); this clip only lets the
   * visitor REPLAY the reply later from the bubble's player button. The
   * continuous-mode mic is released by the worklet's `drained` event, never by
   * this method.
   */
  private finalizeAgentClip(): void {
    const chunks = this.agentRespClipChunks;
    this.agentRespClipChunks = [];
    const sess = this.session;
    if (!sess || sess.nearRealtime || sess.modality !== 'voice') return;
    if (!chunks.length) return;
    const url = pcm16Base64ChunksToWavUrl(chunks);
    if (!url) return;
    const id = this.lastAgentMsgId;
    if (id) this.ui?.setMessageAudio(id, url);
  }

  /** Drop the current response's accumulated (clip) audio. */
  private discardAgentAudio(): void {
    this.agentRespClipChunks = [];
  }

  /** Stop the agent mid-utterance: clear local playback and cancel the server response. */
  private interruptAgent(): void {
    this.localWorkingFiller?.endTurn();
    this.stopUrlAudio();
    this.reportPlayback(false);
    this.clearMicRestoreTimer();
    this.agentSpeaking = false;
    this.agentResponseDone = false;
    this.playbackDrained = false;
    this.discardAgentAudio();
    try { this.audio?.clearPlayback(); } catch { /* */ }
    // Pause any reply clip the visitor was replaying so it can't keep playing
    // after the interrupt.
    try { this.ui?.stopClipPlayback(); } catch { /* */ }
    this.ui?.setAgentSpeaking(false);
    // The agent is no longer speaking: drop the ■ STOP affordance and ignore the
    // cancelled response's trailing audio so it can't overlap the next reply.
    this.agentAudioActive = false;
    this.droppedResponseId = this.currentAudioResponseId;
    this.ui?.hideAgentStop();
    this.ui?.setAvatarSpeaking(false);
    const sess = this.session;
    if (sess && !sess.nearRealtime) {
      this.enqueueAudioSend(() => this.api.ingest(sess.token, {
        callId: sess.callId,
        type: 'interrupt',
      }));
    }
  }

  /**
   * Visitor tapped the ■ STOP button on the agent bubble: stop the reply (local
   * playback + cancel the server response) WITHOUT opening the mic. In
   * continuous open-mic mode the mic is left muted in the visitor's intended
   * state so a stop never starts recording (unlike a barge-in tap).
   */
  private stopAgentPlayback(): void {
    this.interruptAgent();
    // Near-RT / greeting replies play through a URL-audio element rather than the
    // worklet, so stop that too.
    this.stopUrlAudio();
    const sess = this.session;
    if (sess && !sess.nearRealtime && sess.captureMode === 'continuous') {
      this.userMicMuted = true;
      this.audio?.setMuted(true);
      this.ui?.setMicRecording(false);
      this.ui?.setMicMuted(true);
    }
    this.ui?.setStatus(this.cfg?.branding.greeting || this.ui?.tr('connected', 'Connected') || '');
  }

  /** Realtime hold-to-talk: caller pressed the mic button — start forwarding. */
  private handleMicDown(): void {
    if (!this.ui) return;
    const sess = this.session;
    if (!sess || sess.nearRealtime || sess.captureMode !== 'push-to-talk') return;
    this.micHeld = true;
    this.capturedThisTurn = false;
    // Start a fresh caller voice-clip capture for this turn.
    this.userClipChunks = [];
    // Pressing to talk barges in: stop any in-flight agent response (local +
    // server) so the caller's turn isn't appended behind the agent's. In
    // push-to-talk the agent never opens the mic (agentSpeaking stays false),
    // so also cancel whenever a response is still generating server-side —
    // otherwise its trailing audio interleaves with the next reply (overlap).
    if (this.agentSpeaking || this.agentResponseActive || this.workingFillerAudioActive) this.interruptAgent();
    else { this.discardAgentAudio(); try { this.audio?.clearPlayback(); } catch { /* */ } }
    this.agentResponseActive = false;
    this.ui.setMicRecording(true);
    this.ui.setStatus(this.ui.tr('listening', 'Listening…'));
  }

  /** Realtime hold-to-talk: caller released the mic button — commit the turn. */
  private handleMicUp(): void {
    if (!this.ui) return;
    const sess = this.session;
    if (!sess || sess.nearRealtime || sess.captureMode !== 'push-to-talk') return;
    if (!this.micHeld) return;
    this.ui.setMicRecording(false);
    this.ui.setStatus(this.ui.tr('thinking', 'Thinking…'));
    // Flush any audio still buffered in the capture pipeline BEFORE releasing the
    // hold gate so trailing speech is appended, then commit AFTER all appends via
    // the serialized chain — a commit that overtakes its audio yields no reply.
    void (async () => {
      try { await this.audio?.flushPending(); } catch { /* */ }
      this.micHeld = false;
      if (!this.capturedThisTurn) {
        // Nothing captured (tap too brief) — don't commit an empty buffer.
        this.userClipChunks = [];
        this.ui?.setStatus(this.ui.tr('connected', 'Connected'));
        return;
      }
      // Build the caller's voice clip from the captured PCM; it is attached to
      // the spoken transcript bubble when the bridge echoes it back.
      this.pendingUserClipUrl = pcm16Base64ChunksToWavUrl(this.userClipChunks);
      this.userClipChunks = [];
      // A response.create follows the commit server-side — mark the agent
      // response active so a subsequent barge-in cancels it cleanly.
      this.agentResponseActive = true;
      // A new user turn is being committed: stop dropping agent audio so the
      // upcoming reply plays.
      this.droppedResponseId = null;
      this.enqueueAudioSend(() => this.api.ingest(sess.token, {
        callId: sess.callId,
        type: 'audio.commit',
      }));
    })();
  }

  /** Near-RT push-to-talk: first tap records, second tap stops and submits. */
  private async handleNearRealtimePushToTalk(): Promise<void> {
    if (!this.ui) return;
    if (!this.audio) this.audio = new WidgetAudio();
    const audio = this.audio;
    if (!audio.isRecording()) {
      // Barge-in: silence any reply/greeting still playing before opening the
      // mic so the visitor's recording can't capture (and overlap) it.
      this.stopUrlAudio();
      await audio.startUtterance();
      this.ui.setMicRecording(true);
      this.ui.setStatus(this.ui.tr('listening', 'Listening… tap to send'));
      return;
    }
    this.ui.setMicRecording(false);
    let captured: { base64: string; mimeType: string } | null = null;
    try { captured = await audio.stopUtterance(); }
    catch { captured = null; }
    if (!captured) {
      this.ui.setStatus(this.cfg?.branding.greeting || this.ui.tr('connected', 'Connected'));
      return;
    }
    await this.submitNearRealtimeAudioTurn(captured.base64, captured.mimeType);
  }

  /** Submit a recorded near-RT utterance for STT + reply, then play the answer. */
  private async submitNearRealtimeAudioTurn(audioBase64: string, audioMimeType: string): Promise<void> {
    const sess = this.session;
    if (!sess || !this.ui) return;
    // Show the caller's recorded clip (WhatsApp-style) IMMEDIATELY so the user
    // turn is visible while STT + the agent reply are still in flight, instead
    // of only appearing once the whole (potentially slow) turn completes.
    const userClipUrl = `data:${audioMimeType || 'audio/webm'};base64,${audioBase64}`;
    const customerTimestamp = new Date().toISOString();
    this.ui.appendMessage({
      id: `u-${Date.now()}`,
      role: 'user',
      text: '',
      ts: customerTimestamp,
      audioUrl: userClipUrl,
      kind: 'voice',
    });
    this.ui.setStatus(this.ui.tr('thinking', 'Thinking…'));
    const finishDirectResponse = this.beginDirectResponse(sess);
    const stopWorkingFiller = this.beginNearRealtimeWorkingFiller();
    try {
      const res = await this.api.nearRealtimeTurn(sess.token, { audioBase64, audioMimeType });
      if (!this.isCurrentSession(sess)) return;
      this.ui.appendMessage({
        id: `a-${Date.now()}`,
        role: 'agent',
        text: res.reply || '',
        ts: new Date().toISOString(),
        audioUrl: res.audioUrl,
      });
      this.relayNearRtTurn(res.userTranscript, res, customerTimestamp);
      this.renderNearRtDirectives(res.renderDirectives);
      if (res.audioUrl) this.playUrlAudio(res.audioUrl);
      this.ui.setStatus(this.cfg?.branding.greeting || this.ui.tr('connected', 'Connected'));
      if (res.handoff) this.handleNearRtHandoff(res.handoff);
      if (res.transferred && res.transfer) this.handleTransferDirective(res.transfer);
      if (res.ended) {
        this.ui.setStatus(this.ui.tr('conversationEnded', 'Conversation ended'));
        this.concludeConversation('agent_ended').catch(() => { /* */ });
      }
    } catch (err: any) {
      if (this.isCurrentSession(sess)) this.handleNearRtTurnError(err);
    } finally {
      stopWorkingFiller();
      finishDirectResponse();
    }
  }

  /**
   * Gracefully handle a failed near-RT turn. Transient problems (the proxy/
   * runtime timing out or being briefly unreachable) must NOT break the
   * conversation — keep the session alive and invite the caller to retry.
   * Genuine, non-recoverable errors still surface the underlying message.
   */
  private handleNearRtTurnError(err: any): void {
    if (!this.ui) return;
    if (err?.code === 'CHAT_IVA_OWNERSHIP_LOST') {
      void this.connectHumanChat();
      return;
    }
    const slug: string | undefined = err?.code;
    const status: number | undefined = err?.status;
    const transient =
      status === 502 || status === 503 || status === 504 ||
      slug === 'runtime_unreachable' || slug === 'turn_failed' ||
      status === undefined; // network/abort with no HTTP status
    if (transient) {
      this.ui.appendMessage({
        id: `sys-${Date.now()}`,
        role: 'agent',
        text: this.ui.tr('tryAgain', 'Sorry, that took longer than expected. Please try again.'),
        ts: new Date().toISOString(),
      });
      this.ui.setStatus(this.cfg?.branding.greeting || this.ui.tr('connected', 'Connected'));
      return;
    }
    this.ui.setError(this.ui.errorMessage(err));
  }

  private async handleFileSelect(files: File[]): Promise<void> {
    if (!this.session || !this.ui || !this.cfg) return;
    const sess = this.session;
    const maxBytes = this.cfg.files?.maxFileSizeBytes || 10 * 1024 * 1024;
    for (const file of files) {
      const localId = `up-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      this.ui.upsertFileChip({ id: localId, name: file.name, mimeType: file.type, size: file.size, status: 'uploading' });
      if (file.size > maxBytes) {
        this.ui.upsertFileChip({ id: localId, name: file.name, mimeType: file.type, size: file.size, status: 'failed', message: widgetErrorMessage('File too large', this.cfg.language) });
        continue;
      }
      // Count this upload as in-flight so the send button stays disabled until it settles.
      this.uploadsInFlight++;
      this.syncComposerFilesState();
      try {
        const contentBase64 = await readAsBase64(file);
        const resp = await this.api.uploadFile(sess.token, {
          callId: sess.callId,
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          contentBase64,
        });
        if (!this.isCurrentSession(sess)) continue;
        // The visitor removed this attachment while it was still uploading — drop the result.
        if (this.removedUploads.has(localId)) {
          this.removedUploads.delete(localId);
          continue;
        }
        const finalStatus = resp.status === 'uploaded' ? 'ready' : 'failed';
        this.ui.renameFileChip(localId, resp.fileId);
        this.ui.upsertFileChip({
          id: resp.fileId,
          name: resp.name,
          mimeType: resp.mimeType,
          size: resp.size,
          status: finalStatus,
          message: resp.status === 'uploaded' ? undefined : widgetErrorMessage(null, this.cfg.language, 'upload'),
        });
        if (finalStatus === 'ready') {
          this.pendingFiles.push({ id: resp.fileId, name: resp.name, mimeType: resp.mimeType, size: resp.size });
        }
      } catch (err: any) {
        if (this.removedUploads.has(localId)) {
          this.removedUploads.delete(localId);
        } else {
          this.ui.upsertFileChip({ id: localId, name: file.name, mimeType: file.type, size: file.size, status: 'failed', message: widgetErrorMessage(err, this.cfg.language, 'upload') });
        }
      } finally {
        this.uploadsInFlight = Math.max(0, this.uploadsInFlight - 1);
        this.syncComposerFilesState();
      }
    }
  }

  /** Visitor tapped the × on a file chip — drop it from the pending set and UI. */
  private handleFileRemove(id: string): void {
    if (!this.ui) return;
    // If it's still uploading, mark it so the completing upload discards its result.
    if (id.startsWith('up-')) this.removedUploads.add(id);
    this.pendingFiles = this.pendingFiles.filter(f => f.id !== id);
    this.ui.removeFileChip(id);
    this.syncComposerFilesState();
  }

  /** Keep the composer's send button in sync with upload/ready attachment state. */
  private syncComposerFilesState(): void {
    this.ui?.setFilesComposerState(this.uploadsInFlight > 0, this.pendingFiles.length > 0);
  }

  // ── Session lifecycle ─────────────────────────────────────────────────

  private resolveModality(requested: WidgetModality): WidgetModality {
    if (!this.cfg) return requested;
    const allowed: WidgetModality[] = [];
    if (this.cfg.modalities.text) allowed.push('text');
    if (this.cfg.modalities.voice) allowed.push('voice');
    if (allowed.includes(requested)) return requested;
    const mods: any = this.cfg.modalities;
    const fallback: WidgetModality | undefined = mods?.defaultMode || mods?.default;
    if (fallback && allowed.includes(fallback)) return fallback;
    return allowed[0] || requested;
  }

  private async startSession(rawModality: WidgetModality, precall: Record<string, any>): Promise<void> {
    if (!this.cfg || !this.ui || this.startupAttempt || this.humanPreparing || this.session) return;
    const attempt = {};
    this.startupAttempt = attempt;
    const modality = this.resolveModality(this.opts.modality || rawModality);
    this.ui.setStatus(this.ui.tr('connecting', 'Connecting…'));
    // Show a friendly loading panel while the session, tools and knowledge spin
    // up — cleared on the first agent message (greeting) or when the bridge is ready.
    const loadingMsg = (this.cfg.behavior as any)?.loadingMessage
      || this.ui.tr('connecting', 'Connecting…');
    this.ui.showLoading(loadingMsg);

    let jwt: IssueJwtResponse;
    if (this.opts.devTokenResponse) {
      // Admin tester path — bypass embedSecret/Origin checks.
      jwt = this.opts.devTokenResponse;
    } else {
      try {
        jwt = await this.api.issueSessionJwt({
          publicKey: this.opts.publicKey,
          embedSecret: this.opts.embedSecret || '',
          modality,
          precallFormData: precall,
          capturedMetadata: this.widgetCapturedMetadata,
          consentEvidence: this.widgetConsentEvidence,
        });
      } catch (err: any) {
        if (this.startupAttempt !== attempt) return;
        this.startupAttempt = null;
        this.ui.hideLoading();
        this.ui.setError(this.ui.errorMessage(err));
        return;
      }
    }
    if (this.startupAttempt !== attempt) return;
    const ownsSession = await this.claimHumanSession(jwt.sid);
    if (this.startupAttempt !== attempt) return;
    if (!ownsSession) {
      this.startupAttempt = null;
      this.ui.hideLoading();
      this.ui.setError(humanChatLabel(this.cfg?.language, 'sessionLocked'));
      return;
    }
    this.allowedModalities = jwt.modalities;

    let start: StartSessionResponse;
    // Capture mode is a WIDGET setting (cfg.behavior.audioCaptureMode):
    //   - 'push-to-talk' → hold the mic button to talk (release commits)
    //   - 'continuous'   → hands-free: the mic stays open and SERVER VAD decides
    //     turn boundaries, so the visitor never taps to send. The mic button is
    //     a plain mute toggle. Echo is handled locally by auto-muting while the
    //     agent verbalizes, and on the bridge by gating turn detection until the
    //     greeting response completes.
    const captureMode: 'push-to-talk' | 'continuous' =
      this.cfg.behavior?.audioCaptureMode === 'continuous' ? 'continuous' : 'push-to-talk';
    const pushToTalk = captureMode === 'push-to-talk';
    const responseMode = (this.cfg.modalities as any)?.responseMode;
    if (!this.testerMode && !this.humanRecoveryStore()?.interrupt()) {
      this.startupAttempt = null;
      this.ui.hideLoading();
      this.ui.setError(humanChatLabel(this.cfg?.language, 'unavailable'));
      return;
    }
    try {
      start = await this.api.startSession(jwt.token, {
        modality: jwt.modality,
        widgetSessionId: jwt.sid,
        pushToTalk,
        responseMode: ['text', 'audio', 'audio_text', 'match'].includes(responseMode) ? responseMode : undefined,
        // The JWT endpoint only persists the answers on the session row; the
        // runtime reads them from the START body to seed the agent context.
        precallFormData: precall && Object.keys(precall).length ? precall : undefined,
        capturedMetadata: this.widgetCapturedMetadata,
        consentEvidence: this.widgetConsentEvidence,
      });
    } catch (err: any) {
      if (this.startupAttempt !== attempt) return;
      this.startupAttempt = null;
      this.ui.hideLoading();
      if (!this.testerMode && await this.restoreHumanChat()) return;
      this.ui.setError(this.ui.errorMessage(err));
      return;
    }

    if (this.startupAttempt !== attempt) return;
    this.startupAttempt = null;
    this.session = { token: jwt.token, sid: jwt.sid, callId: start.callId, modality: jwt.modality, captureMode };
    this.humanRecoveryStore()?.write({ ...this.session, checking: true });
    this.pendingDirectResponses = 0;
    this.lastSession = this.session;
    this.concluded = false;
    this.sessionContext = {};
    this.rememberContext(precall);
    for (const [key, entry] of Object.entries(this.widgetCapturedMetadata)) this.rememberContext({ [key]: entry?.value });
    // New session — re-arm the page-unload end handler.
    this.endBeaconSent = false;
    this.ui.setSessionActive(true);

    // Near-real-time agents are request/response — no Realtime WS bridge, no
    // continuous audio streaming. Render the greeting and wait for turns.
    const isNearRealtime = start.config?.executionMode === 'near-realtime' || !!start.sessionId;
    if (isNearRealtime) {
      this.session.nearRealtime = true;
      this.ui.hideLoading();
      this.nearRealtimeWorkingFiller = start.workingFiller || null;
      this.localWorkingFiller?.dispose();
      this.localWorkingFiller = null;
      this.lastSession = this.session;
      // Near-RT voice uses push-to-talk capture instead of continuous
      // streaming — set up the recorder so the mic control works identically
      // (from the user's perspective) to a realtime voice agent.
      if (jwt.modality === 'voice') {
        this.audio = new WidgetAudio();
        this.ui.setMicMode('push-to-talk');
      }
      const greeting = start.greeting || this.cfg.branding.greeting || '';
      if (greeting) {
        this.ui.appendMessage({
          id: `g-${Date.now()}`,
          role: 'agent',
          text: greeting,
          ts: new Date().toISOString(),
        });
        // Surface the greeting in the tester's Transcrição tab too — near-RT
        // greetings never flow through a turn relay otherwise.
        this.relay('pubsub.event', { event: { type: 'transcript.turn', turn: { role: 'agent', text: greeting, timestamp: new Date().toISOString() } } });
      }
      // Greeting AI Credits ledger rows ({{ai:}} span generation + greeting TTS)
      // — relayed to the tester's Custo tab the same way turn consumption is,
      // since the greeting is billed at session start and never hits a turn relay.
      if (this.testerMode) {
        for (const ev of start.consumption?.events || []) {
          this.relay('pubsub.event', {
            event: {
              type: 'consumption.event',
              feature: ev.feature,
              operationType: ev.operationType,
              model: ev.model,
              credits: ev.credits,
              totalTokens: ev.totalTokens,
              ttsUsage: ev.ttsUsage,
              sttUsage: ev.sttUsage,
              sessionCredits: start.consumption?.sessionCredits,
              timestamp: ev.timestamp,
            },
          });
        }
      }
      if (start.greetingAudioUrl) this.playUrlAudio(start.greetingAudioUrl);
      if (start.pills && start.pills.length) this.ui.renderPills(start.pills);
      this.ui.setStatus(this.cfg.branding.greeting || this.ui.tr('connected', 'Connected'));
      this.relay('session.started', {
        sid: jwt.sid,
        callId: start.callId,
        sessionId: start.sessionId,
        modality: jwt.modality,
        executionMode: 'near-realtime',
        allowedModalities: this.allowedModalities,
        language: this.cfg.language,
        branding: this.cfg.branding,
        configName: start.config?.name || this.cfg.name,
      });
      return;
    }

    this.transport = new WidgetTransport();
    this.transport.onMessage((data) => this.handlePubSubEvent(data));
    try {
      await this.transport.connect(start.pubsubUrl);
    } catch (err: any) {
      this.ui.setError(widgetErrorMessage(err, this.cfg?.language, 'connection'));
      return;
    }
    // We're attached to the bridge group. `bridge.ready` is the intended signal
    // to clear the connecting panel, but it is frequently published BEFORE this
    // client finishes joining the group (the bridge runs server-side and is
    // already live by the time startSession returns) — so it's missed and the
    // spinner would hang until the greeting's first audio/text arrives (and not
    // at all if that audio is delayed or dropped). Connecting is itself proof the
    // session is live, so clear the panel now; the greeting renders right after.
    this.ui.hideLoading();

    if (jwt.modality === 'voice') {
      if (this.session) this.session.pushToTalk = pushToTalk;
      this.lastSession = this.session;
      this.micHeld = false;
      this.agentSpeaking = false;
      this.agentResponseDone = false;
      // The bridge auto-creates the greeting response at session start — treat
      // the agent response as active so an early push-to-talk barge-in cancels
      // the greeting instead of letting it stream over the caller's first turn.
      this.agentResponseActive = true;
      this.playbackDrained = false;
      this.clearMicRestoreTimer();
      this.audio = new WidgetAudio();
      this.audio.setOnFrame((b64) => {
        const sess = this.session;
        if (!sess) return;
        // Forward captured audio only while the current turn is "live":
        //   push-to-talk → while the caller HOLDS the mic button
        //   continuous   → while the mic is OPEN (not muted)
        // Outside a live turn we drop frames so the agent's own playback can't
        // re-enter the open mic (echo).
        const live = sess.captureMode === 'continuous' ? !this.audio!.isMuted() : this.micHeld;
        if (!live) return;
        this.capturedThisTurn = true;
        // Retain captured frames (push-to-talk AND continuous) so the caller's
        // spoken turn can be shown as a WhatsApp-style voice clip rather than
        // only its transcription. In continuous mode the mic is auto-muted while
        // the agent speaks, so this only ever accumulates the visitor's speech.
        this.userClipChunks.push(b64);
        // Serialize so a later commit can never overtake these appends.
        this.enqueueAudioSend(() => this.api.ingest(sess.token, {
          callId: sess.callId,
          type: 'audio',
          payload: b64,
        }));
      });
      // Restore the open mic to the visitor's intended state once the agent
      // finishes verbalizing locally (continuous open-mic anti-echo).
      this.audio.setOnDrained(() => {
        this.reportPlayback(false);
        // Agent audio finished playing: drop the ■ STOP button (mode-agnostic).
        this.agentAudioActive = false;
        this.ui?.hideAgentStop();
        this.ui?.setAvatarSpeaking(false);
        this.playbackDrained = true;
        this.maybeFinishAgentTurn();
        this.armMicRestoreWatchdog();
      });
      this.audio.setOnPlaybackLevel((level) => this.ui?.setVoiceAvatarLevel(level));
      // Live input meter under the composer (both capture modes), mirroring the
      // AI IVR tester's VU meter. Reads 0 whenever the mic isn't capturing.
      this.audio.setOnCaptureMetrics((_samples, rms) => {
        const capturing = captureMode === 'continuous' ? !this.audio?.isMuted() : this.micHeld;
        this.ui?.setMicLevel(capturing ? Math.min(1, rms * 3.5) : 0);
      });
      try { await this.audio.start(); }
      catch (err: any) {
        this.ui.setError(widgetErrorMessage(err, this.cfg?.language, 'microphone'));
      }
      if (captureMode === 'push-to-talk') {
        // Hold-to-talk: the caller drives turns by holding the mic button;
        // start gated and show the hold-to-talk affordance.
        this.ui.setMicMode('hold-to-talk');
      } else {
        // Hands-free: keep the mic CLOSED through the greeting. Server VAD is
        // live, so capturing here would buffer the agent's own greeting (and
        // room noise) and fire a second response that talks over it.
        // maybeFinishAgentTurn opens the mic automatically once the greeting
        // drains, because userMicMuted is already false.
        this.userMicMuted = false;
        this.audio.setMuted(true);
        this.ui.setMicMode('mute');
        this.ui.setMicMuted(true);
      }
      this.ui.setVoiceAvatarActive(true);
      this.resetAvatarIdle();
    }

    this.ui.setStatus(this.cfg.branding.greeting || this.ui.tr('connected', 'Connected'));
    this.relay('session.started', {
      sid: jwt.sid,
      callId: start.callId,
      modality: jwt.modality,
      allowedModalities: this.allowedModalities,
      language: this.cfg.language,
      branding: this.cfg.branding,
      configName: start.config?.name || this.cfg.name,
    });
  }

  private handlePubSubEvent(data: any): void {
    if (!data || typeof data !== 'object' || !this.ui) return;
    if (data.type === 'chat.handoff') { void this.connectHumanChat(); return; }
    if (data.type === 'chat.preparing') {
      if (!this.session || this.humanPreparing || this.humanMode) return;
      this.humanPreparing = true;
      this.humanDraft = this.ui.getDraft();
      this.persistHumanRecovery();
      this.humanPreparationAttempts = 0;
      this.scheduleHumanPreparationRecovery();
      this.micHeld = false;
      this.userMicMuted = true;
      this.clearMicRestoreTimer();
      this.discardAgentAudio();
      this.stopUrlAudio();
      this.audio?.setMuted(true);
      const audio = this.audio;
      this.audio = null;
      void Promise.resolve().then(() => audio?.stop()).catch(() => {});
      this.ui.setSendEnabled(false);
      this.ui.discardPartial('agent');
      this.ui.setStatus(humanChatLabel(this.cfg?.language, 'handoff_pending'));
      return;
    }
    if (this.humanPreparing) {
      if (data.type === 'bridge.error' || data.type === 'bridge.fatal') this.ui?.setError(humanChatLabel(this.cfg?.language, 'unavailable'));
      return;
    }
    if (this.humanMode) return;
    if (this.workingFillerAudioActive && this.workingFillerIsSpeech
      && (['audio.delta', 'agent.text.delta', 'agent.text.done', 'cost.snapshot'].includes(data.type)
        || (data.type === 'transcript.turn' && data.turn?.role === 'agent'))) {
      this.pendingAfterFiller.push(() => this.handlePubSubEvent(data));
      return;
    }
    // Tester data feed: relay every Realtime/bridge event upstream so the
    // admin tester can populate its Transcrição / Tools / Cost / Files tabs
    // without opening a second PubSub connection. Production embeds never
    // relay (testerMode === false).
    this.relay('pubsub.event', { event: data });
    // Receiving ANY bridge event proves the realtime session is live, so clear
    // the "connecting" panel here. `bridge.ready` is the intended signal, but it
    // can be published BEFORE the widget's PubSub client finishes joining the
    // group (race) — in that case it's missed and the only other dismissals
    // (audio.delta / transcript) could be skipped (e.g. the audio drop-guard),
    // leaving the spinner stuck after the greeting is dispatched. Idempotent.
    this.ui.hideLoading();
    switch (data.type) {
      case 'context.field.saved':
        if (typeof data.field === 'string') this.rememberContext({ [data.field]: data.value });
        break;
      case 'bridge.ready':
        this.ui.setStatus(this.ui.tr('ready', 'Ready'));
        this.resetAvatarIdle();
        break;
      case 'working.started':
        if (this.session?.modality === 'voice' && typeof data.audioUrl === 'string' && /^https:\/\//i.test(data.audioUrl)) {
          if (this.urlAudio || this.agentAudioActive || this.micHeld) break;
          this.workingFillerIsSpeech = data.mode === 'vocal_filler';
          this.playUrlAudio(data.audioUrl, undefined, true);
        }
        break;
      case 'working.stopped':
        if (this.workingFillerAudioActive && !this.workingFillerIsSpeech) {
          this.workingFillerAudioActive = false;
          this.stopUrlAudio();
        }
        break;
      case 'audio.delta':
        if (data.audio) {
          if (this.workingFillerAudioActive) {
            this.workingFillerAudioActive = false;
            this.stopUrlAudio();
          }
          // After a barge-in the cancelled response keeps emitting trailing
          // audio for a beat. Drop only that response's chunks — anything newer
          // is the reply the visitor is waiting for.
          const respId = (data as any).responseId || null;
          if (respId && respId === this.droppedResponseId) break;
          this.currentAudioResponseId = respId;
          if (!this.agentAudioActive) this.reportPlayback(true);
          this.ui.hideLoading();
          // Stream the PCM straight into the worklet for low-latency playback,
          // and ALSO buffer it so the finished reply can be attached to the
          // agent bubble as a replayable voice clip. The worklet's `drained`
          // event (setOnDrained) releases the continuous-mode mic once playback
          // ends, so live streaming must keep feeding it.
          this.audio?.enqueuePlayback(data.audio);
          this.agentRespClipChunks.push(data.audio);
          // A response is now generating server-side — barge-in must cancel it.
          this.agentResponseActive = true;
          // First audio of this response: show the icon-only ■ STOP button on the
          // agent bubble so the visitor can stop it mid-reply in every mode.
          if (!this.agentAudioActive) {
            this.agentAudioActive = true;
            this.ui.showAgentStop(this.lastAgentMsgId);
            // Avatar reacts to the agent's voice in EVERY capture mode; the
            // level feed below is ignored unless the state is 'speaking'.
            this.ui.setAvatarSpeaking(true);
          }
          // Continuous open-mic anti-echo: mute the live mic while the agent
          // verbalizes; pause any voice clip the visitor was replaying.
          this.onAgentAudioActivity();
          this.ui?.stopClipPlayback();
        }
        break;
      case 'transcript.turn':
        if (data.turn?.text) {
          // The bridge labels the caller's turn `customer` (and the model's
          // turn `agent`/`assistant`). Anything that isn't the agent is the
          // user. A typed user message is already rendered locally by
          // handleSendText (for both text and voice agents), so suppress the
          // matching bridge echo to avoid a duplicate; spoken user turns are
          // never in pendingUserEchoes and still render here.
          const role = data.turn.role === 'agent' || data.turn.role === 'assistant' ? 'agent' : 'user';
          // A spoken visitor turn opens a new exchange — nothing left to drop.
          if (role === 'user') this.droppedResponseId = null;
          let isEcho = false;
          if (role === 'user') {
            const idx = this.pendingUserEchoes.indexOf(String(data.turn.text).trim());
            if (idx !== -1) {
              this.pendingUserEchoes.splice(idx, 1);
              isEcho = true;
            }
          } else {
            // Realtime TEXT mode already renders the agent turn via the streamed
            // `agent.text.delta`/`agent.text.done` partial. The bridge ALSO emits
            // a `transcript.turn` for the same text (needed by the tester tabs and
            // by realtime VOICE, which has no text stream). Suppress this echo in
            // realtime text mode so the agent reply is not rendered twice. Near-RT
            // and voice still rely on this event, so only skip realtime-text.
            const isRealtimeText = this.session?.modality === 'text' && !this.session?.nearRealtime;
            if (isRealtimeText) isEcho = true;
          }
          if (!isEcho) {
            const msgId = `t-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
            if (role === 'agent') this.lastAgentMsgId = msgId;
            // A spoken caller turn: show the recorded voice clip (WhatsApp-style)
            // in place of the transcribed text. Falls back to text when no clip.
            const userClip = role === 'user' ? this.pendingUserClipUrl : null;
            if (role === 'user') this.pendingUserClipUrl = null;
            this.ui.appendMessage({
              id: msgId,
              role,
              text: userClip ? '' : data.turn.text,
              ts: new Date().toISOString(),
              audioUrl: userClip || undefined,
              kind: userClip ? 'voice' : 'text',
            });
          }
        }
        break;
      case 'agent.text.delta':
        this.partialAgent += data.delta || '';
        // Realtime TEXT mode renders only the FULL response (on agent.text.done)
        // to avoid orphaned streaming partials — a half-streamed second text item
        // or a cancelled response leaves a stranded gray bubble. Show a typing
        // indicator while the model streams instead. Other paths keep the live
        // partial bubble (near-RT renders inline; voice has no text stream).
        if (this.session?.modality === 'text' && !this.session?.nearRealtime) {
          this.agentBusy = true;
          this.bumpTyping();
        } else {
          this.ui.updatePartial('agent', this.partialAgent);
        }
        break;
      case 'agent.text.done': {
        const doneText = data.text || this.partialAgent;
        const isRealtimeText = this.session?.modality === 'text' && !this.session?.nearRealtime;
        if (isRealtimeText) {
          // Realtime text renders the FULL message here. Drop empty items and
          // bare-JSON items (the model sometimes leaks tool-call arguments as
          // assistant text) so they never appear as a stray gray bubble.
          const trimmed = (doneText || '').trim();
          let looksJson = false;
          if (trimmed.length > 1 && ((trimmed[0] === '{' && trimmed[trimmed.length - 1] === '}') || (trimmed[0] === '[' && trimmed[trimmed.length - 1] === ']'))) {
            try { JSON.parse(trimmed); looksJson = true; } catch { looksJson = false; }
          }
          if (trimmed && !looksJson) this.ui.commitPartial('agent', doneText);
          else this.ui.discardPartial('agent');
        } else {
          this.ui.commitPartial('agent', doneText);
        }
        this.partialAgent = '';
        this.committedAgentThisTurn = true;
        // Committing rendered the message and cleared the typing dots. Re-arm the
        // watchdog: if more text or a tool follows it stays up, otherwise the
        // idle timer hides it (cost.snapshot hides it instantly in the normal case).
        this.bumpTyping();
        break;
      }
      case 'cost.snapshot':
        // Realtime turn boundary (the bridge emits this at response.done).
        // Finalize any lingering agent partial so it can never be reused at a
        // stale DOM position by the next turn (which caused the agent reply to
        // render ABOVE a newer user message). If a real agent.text.done already
        // committed this turn, a leftover partial is a stray duplicate fragment
        // → discard it; otherwise commit it as the turn's only text.
        if (this.ui.hasPartial('agent')) {
          if (this.committedAgentThisTurn) this.ui.discardPartial('agent');
          else this.ui.commitPartial('agent', this.partialAgent);
        }
        this.ui.sweepStrayPartials();
        // The turn yields (typing dots stop) only when the response completes
        // with NO tool still running. This is the fast-path instant hide; if it
        // never arrives the watchdog still clears the dots after a short idle.
        if (this.toolsInFlightIds.size === 0) this.stopTyping();
        else this.bumpTyping();
        this.partialAgent = '';
        this.committedAgentThisTurn = false;
        // Realtime voice audio finalization. The bridge sets `continues: true`
        // when it will force a continuation (an unrelayed tool/MCP result or a
        // skipped flow step) — this response is a superseded deferral, so DROP
        // its streamed audio and clip so only the final answer plays/keeps a
        // clip. Otherwise this is the final response: attach its replayable clip.
        if ((data as any).continues === true) {
          // A continuation is coming, but the visitor has NOT heard this reply
          // yet: the model finishes generating seconds before playback catches
          // up. Cutting here truncates it mid-word, so leave the queue alone and
          // let the continuation play after it. The turn isn't over, so the
          // speaking gate and barge-in stay armed.
          this.finalizeAgentClip();
        } else {
          this.finalizeAgentClip();
          // The response is finished server-side; barge-in no longer needs to
          // cancel it (and shouldn't fire a stray response.cancel).
          this.agentResponseActive = false;
          // Continuous open-mic: the agent turn is complete. Once local playback
          // also drains, restore the visitor's intended mic state (anti-echo).
          this.agentResponseDone = true;
          this.maybeFinishAgentTurn();
        }
        break;
      case 'tool.call':
        // A tool was dispatched — keep the typing dots up through the (possibly
        // long) execution, even though the pre-tool message already committed.
        if (this.session?.modality === 'text' && !this.session?.nearRealtime) {
          const id = String((data as any).toolCallId ?? `tc-${Date.now()}-${Math.random().toString(36).slice(2)}`);
          this.toolsInFlightIds.add(id);
          this.agentBusy = true;
          this.bumpTyping();
        }
        break;
      case 'tool.result':
        if (this.session?.modality === 'text' && !this.session?.nearRealtime) {
          // A real tool result arrived mid-turn. Clear its in-flight slot, then
          // keep the dots up only while work remains (another tool running or
          // the agent still busy producing the continuation). The following
          // cost.snapshot is the authoritative turn boundary that finally hides
          // them — so we must NOT force the dots back on after the turn yields.
          const id = String((data as any).toolCallId ?? '');
          if (id) this.toolsInFlightIds.delete(id);
          else if (this.toolsInFlightIds.size) this.toolsInFlightIds.delete(this.toolsInFlightIds.values().next().value as string);
          // Re-arm the watchdog: the continuation reply usually follows; if not,
          // the idle timer hides the dots once activity truly stops. touchTyping
          // never resurrects dots that already yielded.
          this.touchTyping();
        }
        break;
      case 'tool.settled':
        // Terminal settle the bridge emits for every dispatched call (covers
        // branches that send no rich tool.result — transfer, save_context,
        // hand-off, etc.) so the in-flight slot is always released. This event
        // is purely a bookkeeping signal: it must NEVER re-show the typing dots
        // (it can arrive AFTER the turn already yielded), only release the slot
        // and hide the dots if nothing else is pending.
        if (this.session?.modality === 'text' && !this.session?.nearRealtime) {
          const id = String((data as any).toolCallId ?? '');
          if (id) this.toolsInFlightIds.delete(id);
          else if (this.toolsInFlightIds.size) this.toolsInFlightIds.delete(this.toolsInFlightIds.values().next().value as string);
          // Release the slot, then let the watchdog decide: it hides the dots if
          // nothing else is pending and never resurrects them post-turn.
          this.touchTyping();
        }
        break;
      case 'agent.transfer':
        if (data.transfer) this.handleTransferDirective(data.transfer as WidgetTransferDirective);
        break;
      case 'agent.handoff':
        // Agent-to-agent hand-off: the same Realtime session continues with a
        // different Virtual Agent (no redirect, no teardown).
        this.handleNearRtHandoff(data.handoff as { toAgentId: string; toAgentName?: string });
        break;
      case 'render.richContent':
        if (this.ui && data.toolName) {
          if (data.interaction === 'await_submit' && data.toolCallId) {
            this.pendingRich.set(String(data.toolCallId), String(data.toolName));
          }
          this.ui.appendRichContent(data);
        }
        break;
      case 'render.pills':
        if (this.ui && Array.isArray(data.pills)) this.ui.renderPills(data.pills as WidgetPill[]);
        break;
      case 'speech.interrupt':
        // The server detected the visitor barging in over the agent. Stop local
        // playback and clear the agent-speaking auto-mute so the open mic stays
        // live for the visitor's interrupting turn.
        this.discardAgentAudio();
        this.audio?.clearPlayback();
        if (this.agentSpeaking) {
          this.agentSpeaking = false;
          this.agentResponseDone = false;
          this.playbackDrained = false;
          this.ui?.setAgentSpeaking(false);
          this.audio?.setMuted(this.userMicMuted);
          this.ui?.setMicMuted(this.userMicMuted);
        }
        break;
      case 'file.ready':
        // Server-side flips the chip to ✓ via the upload response; this
        // event is a redundancy net for multi-tab / reconnect cases.
        if (data.fileId && data.fileName) {
          this.ui.upsertFileChip({
            id: String(data.fileId),
            name: String(data.fileName),
            mimeType: String(data.mimeType || ''),
            size: 0,
            status: 'ready',
          });
        }
        break;
      case 'call.finalized':
      case 'bridge.closed':
        this.ui.setStatus(this.ui.tr('conversationEnded', 'Conversation ended'));
        this.concludeConversation('remote_closed').catch(() => { /* */ });
        break;
      case 'realtime.error': {
        // The Realtime bridge surfaced a model/transport error (e.g. an empty
        // input-audio commit or an active-response conflict). These are usually
        // recoverable per-turn — log for diagnosis and restore the UI so the
        // visitor can retry instead of being stranded on a silent "Thinking…".
        const err = (data as any).error || {};
        try { console.warn('[widget] realtime.error:', err.code || '', err.message || '', err); } catch { /* */ }
        if (this.agentSpeaking) {
          this.agentSpeaking = false;
          this.agentResponseDone = false;
          this.playbackDrained = false;
          this.ui?.setAgentSpeaking(false);
        }
        this.capturedThisTurn = false;
        // Hands-free idle state is an OPEN mic, so restore the visitor's own
        // intent here — a transient per-turn error must not strand them muted.
        if (this.session?.captureMode === 'continuous') {
          this.audio?.setMuted(this.userMicMuted);
          this.ui?.setMicMuted(this.userMicMuted);
        }
        this.ui?.setStatus(this.ui.tr('connected', 'Connected'));
        break;
      }
      case 'bridge.fatal':
        this.ui.setError(this.ui.errorMessage(data.error || data));
        this.teardownSession('bridge_error').catch(() => { /* */ });
        break;
      case 'bridge.error':
        this.ui.setSessionActive(false);
        this.ui.setSendEnabled(false);
        this.ui.setError(widgetErrorMessage(data.error || data, this.cfg?.language, 'connection'));
        this.teardownSession('bridge_error').catch(() => { /* */ });
        break;
      // Other events are silently ignored — tool calls etc. aren't surfaced
      // in the public widget UI.
    }
  }

  /**
   * Handle a Virtual Agent webhook-transfer handoff: surface the transfer
   * message, notify the host page, and optionally redirect the user from the
   * IVA widget to a human-chat surface.
   */
  private handleTransferDirective(transfer: WidgetTransferDirective): void {
    if (!transfer) return;
    if (transfer.humanChat) { void this.connectHumanChat(); return; }
    if (transfer.message && this.ui) {
      this.ui.appendMessage({
        id: `x-${Date.now()}`,
        role: 'agent',
        text: transfer.message,
        ts: new Date().toISOString(),
      });
    }
    // Notify host integrations (tester relay + production host hook).
    this.relay('transfer', { transfer });
    this.emitHostEvent('transfer', { transfer });

    const redirect = transfer.redirect;
    if (!redirect || !redirect.url) return;
    const delay = typeof redirect.delayMs === 'number' ? Math.max(0, redirect.delayMs) : 2000;
    const go = (): void => {
      if (typeof window === 'undefined') return;
      try {
        if (redirect.target === 'self') {
          window.location.assign(redirect.url);
        } else if (redirect.target === 'blank') {
          window.open(redirect.url, '_blank', 'noopener');
        } else {
          // 'parent' → let the host page perform the swap from the IVA widget
          // to the human-chat widget; the directive was already relayed above.
          this.emitHostEvent('redirect', { url: redirect.url, target: redirect.target });
        }
      } catch { /* */ }
    };
    if (delay > 0 && typeof window !== 'undefined') window.setTimeout(go, delay);
    else go();
  }

  /**
   * Dispatch a host-page hook for production embeds: a same-window DOM
   * CustomEvent (script-tag embeds) and a cross-window postMessage (iframe
   * embeds). Distinct from `relay`, which only fires in the admin tester.
   */
  private emitHostEvent(type: string, detail: Record<string, any>): void {
    if (typeof window === 'undefined') return;
    try {
      window.dispatchEvent(new CustomEvent(`autoqm-widget:${type}`, { detail: { source: 'autoqm-widget', ...detail } }));
    } catch { /* */ }
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ source: 'autoqm-widget', type, ...detail }, '*');
      }
    } catch { /* */ }
  }

  /**
   * Show the realtime-text typing dots and (re)arm the idle watchdog. Called on
   * every agent-activity signal. When the idle timer fires it hides the dots
   * only if no tool is in flight (a long tool call keeps them up); past the hard
   * cap it always hides them. This is the single authoritative show path for
   * realtime text — it self-heals against missing/late/duplicate bridge events.
   */
  private bumpTyping(): void {
    if (!this.ui || !(this.session?.modality === 'text' && !this.session?.nearRealtime)) return;
    if (!this.typingShownAt) this.typingShownAt = Date.now();
    this.ui.showTyping();
    if (this.typingIdleTimer) clearTimeout(this.typingIdleTimer);
    this.typingIdleTimer = setTimeout(() => {
      this.typingIdleTimer = null;
      const elapsed = Date.now() - (this.typingShownAt || Date.now());
      // A tool is still genuinely running → keep the dots and re-check later,
      // unless we've blown past the absolute ceiling (tool that never settles).
      if (this.toolsInFlightIds.size > 0 && elapsed < WidgetController.TYPING_MAX_MS) {
        this.bumpTyping();
        return;
      }
      this.stopTyping();
    }, WidgetController.TYPING_IDLE_MS);
  }

  /** Immediately hide the realtime-text typing dots and disarm the watchdog. */
  private stopTyping(): void {
    if (this.typingIdleTimer) { clearTimeout(this.typingIdleTimer); this.typingIdleTimer = null; }
    this.typingShownAt = 0;
    this.agentBusy = false;
    this.ui?.hideTyping();
  }

  /**
   * Re-evaluate the watchdog WITHOUT resurrecting hidden dots. Used by terminal
   * bookkeeping events (tool.result / tool.settled) that may arrive AFTER the
   * turn already yielded — they must release the slot and let the watchdog hide
   * the dots if idle, but must never flash the dots back on once they're gone.
   */
  private touchTyping(): void {
    if (!(this.session?.modality === 'text' && !this.session?.nearRealtime)) return;
    if (!this.typingShownAt) return; // already hidden — do not resurrect
    this.bumpTyping();
  }

  private humanRecoveryStore(): HumanChatRecoveryStore | null {
    if (this.testerMode) return null;
    try {
      const base = new URL(this.opts.apiBaseUrl || location.origin, location.origin).href.replace(/\/$/, '');
      return new HumanChatRecoveryStore(sessionStorage, JSON.stringify([location.origin, this.opts.publicKey, base]), Date.now, this.opts.publicKey);
    } catch { return null; }
  }

  private async claimHumanSession(sid: string): Promise<boolean> {
    if (this.testerMode) return true;
    try {
      if (!this.sessionLocks) {
        const base = new URL(this.opts.apiBaseUrl || location.origin, location.origin).href.replace(/\/$/, '');
        this.sessionLocks = new HumanChatSessionLock(JSON.stringify([location.origin, this.opts.publicKey, base]), navigator.locks);
      }
      return await this.sessionLocks.acquire(sid);
    } catch { return false; }
  }

  private async restoreHumanChat(): Promise<boolean> {
    if (this.humanRestoreAttempt) return true;
    if (!this.ui || this.session) return false;
    const store = this.humanRecoveryStore();
    const record = store?.read();
    if (!record && !store?.hadCheckpoint) return false;
    this.humanPreparing = true;
    this.ui.openPanel(false);
    this.ui.renderConversation();
    this.ui.setSessionActive(true);
    this.ui.setHumanChatMode();
    this.ui.setSendEnabled(false);
    if (!record) {
      this.ui.setError(humanChatLabel(this.cfg?.language, 'unavailable'));
      return true;
    }
    const attempt = {};
    this.humanRestoreAttempt = attempt;
    const ownsSession = await this.claimHumanSession(record.sid);
    if (this.humanRestoreAttempt !== attempt) return true;
    this.humanRestoreAttempt = null;
    if (!ownsSession) {
      this.ui.setError(humanChatLabel(this.cfg?.language, 'sessionLocked'));
      return true;
    }
    this.session = { token: record.token, sid: record.sid, callId: record.callId, modality: 'text' };
    this.lastSession = this.session;
    this.humanDraft = record.draft || '';
    this.humanSendRetry = record.sendRetry;
    this.humanCommandRetries = record.commandRetries || [];
    this.humanCancelCommand = this.humanCommandRetries.find(command => command.action === 'customer-cancel')?.commandId || null;
    this.humanEndCommand = this.humanCommandRetries.find(command => command.action === 'customer-end')?.commandId || null;
    this.recoveredConversationId = record.conversationId ?? null;
    this.humanReloadRecovery = true;
    this.installUnloadHandler();
    await this.connectHumanChat();
    this.scheduleHumanPreparationRecovery();
    return true;
  }

  private canCancelHumanQueue(): boolean {
    return ['queued', 'offered'].includes(this.humanState?.status || '')
      || (this.humanState?.status === 'human_active' && this.humanCommandRetries.some(command => command.action === 'customer-cancel'));
  }

  private updateHumanSendControls(): void {
    const active = this.humanMode && !this.humanPreparing && !!this.humanState && ['queued', 'offered', 'human_active'].includes(this.humanState.status);
    this.ui?.setSendEnabled(active && !this.humanSending && (!this.humanSendRetry || this.humanDraft.trim() === this.humanSendRetry.text));
    this.ui?.setHumanSendRetry(humanChatLabel(this.cfg?.language, 'retry'), active && !!this.humanSendRetry, this.humanSending);
  }

  private persistHumanRecovery(conversationId = this.humanState?.conversationId || this.recoveredConversationId): boolean {
    if (!this.session) return false;
    if (this.testerMode) return true;
    const content = { ...this.session, draft: this.humanDraft };
    return this.humanRecoveryStore()?.write(conversationId
      ? { ...content, conversationId, commandRetries: this.humanCommandRetries, ...(this.humanSendRetry ? { sendRetry: this.humanSendRetry } : {}) }
      : { ...content, preparing: true }) ?? false;
  }

  private scheduleHumanPreparationRecovery(): void {
    if (!this.humanPreparing || this.humanPreparationTimer) return;
    if (++this.humanPreparationAttempts > 60) {
      this.ui?.setError(humanChatLabel(this.cfg?.language, 'unavailable'));
      return;
    }
    const session = this.session;
    this.humanPreparationTimer = setTimeout(() => {
      this.humanPreparationTimer = null;
      if (this.session !== session || !this.humanPreparing) return;
      void this.connectHumanChat().finally(() => this.scheduleHumanPreparationRecovery());
    }, 2000);
  }

  private async connectHumanChat(): Promise<void> {
    const session = this.session;
    if (!session || !this.ui || this.humanStarting || this.humanChat) return;
    this.humanStarting = true;
    try {
      let state: HumanChatState;
      try {
        state = await this.api.chatCustomer<HumanChatState>(session.token, { action: 'state' });
      } catch (error) {
        const failure = error as { status?: number; code?: string } | null;
        if (this.session !== session || failure?.status !== 401 || failure.code !== 'CHAT_AUTH_REQUIRED') throw error;
        const renewed = await this.api.chatCustomer<{ token: string; exp: number }>(session.token, { action: 'renew' });
        if (this.session !== session) return;
        if (typeof renewed.token !== 'string' || !renewed.token || !Number.isSafeInteger(renewed.exp)
          || renewed.exp * 1000 <= Date.now()) throw new Error('CHAT_INVALID_TOKEN');
        session.token = renewed.token;
        if (this.humanPreparing) this.persistHumanRecovery();
        state = await this.api.chatCustomer<HumanChatState>(session.token, { action: 'state' });
      }
      if (this.session !== session) return;
      const restored = this.recoveredConversationId;
      const reloaded = this.humanReloadRecovery;
      if (state.status === 'iva_active' && (!reloaded || !state.conversationId)) return;
      if (!state.conversationId || (restored && state.conversationId !== restored)
        || !Number.isSafeInteger(state.epoch) || state.epoch! < 0
        || !['iva_active', 'handoff_pending', 'queued', 'offered', 'human_active', 'wrap_up', 'returning_to_iva', 'closed'].includes(state.status)
        || (!reloaded && (!Number.isSafeInteger(state.initialSequence) || state.initialSequence! < 0))) throw new Error('CHAT_INVALID_STATE');
      if (reloaded) this.ui.restoreDraft(this.humanDraft);
      this.humanDraft = this.ui.getDraft();
      this.humanState = state;
      this.persistHumanRecovery(state.conversationId);
      this.humanMode = true;
      this.humanPreparing = false;
      if (this.humanPreparationTimer) clearTimeout(this.humanPreparationTimer);
      this.humanPreparationTimer = null;
      session.modality = 'text';
      this.pendingDirectResponses = 0;
      this.micHeld = false;
      this.userMicMuted = true;
      this.clearMicRestoreTimer();
      if (this.typingIdleTimer) { clearTimeout(this.typingIdleTimer); this.typingIdleTimer = null; }
      this.localWorkingFiller?.dispose();
      this.localWorkingFiller = null;
      this.nearRealtimeWorkingFiller = null;
      this.pendingFiles = [];
      this.pendingRich.clear();
      this.discardAgentAudio();
      this.stopUrlAudio();
      this.audio?.setMuted(true);
      this.ui.setHumanChatMode();
      this.ui.setSendEnabled(false);
      this.ui.discardPartial('agent');
      this.ui.hideLoading();
      const audio = this.audio;
      const transport = this.transport;
      this.audio = null;
      this.transport = null;
      await Promise.allSettled([Promise.resolve().then(() => audio?.stop()), Promise.resolve().then(() => transport?.stop())]);
      if (this.session !== session) return;
      this.ui.appendMessage({ id: `human-${state.conversationId}`, role: 'agent', text: humanChatLabel(this.cfg?.language, 'switched'), ts: new Date().toISOString() });
      const channel = new HumanChatChannel(this.api, session.token, {
        state: next => {
          if (this.session !== session) return;
          this.humanState = next;
          this.ui?.setHumanQueueCancel(humanChatLabel(this.cfg?.language, 'cancel'), this.canCancelHumanQueue(), this.humanCancelBusy);
          this.ui?.setHumanChatStatus(humanChatLabel(this.cfg?.language, 'team'), humanChatLabel(this.cfg?.language, next.status));
          this.updateHumanSendControls();
        },
        messages: messages => {
          if (this.session !== session) return;
          for (const message of messages) this.ui?.appendMessage({ id: message.id, role: message.sender === 'customer' ? 'user' : 'agent',
            text: message.text, ts: new Date(message.createdAt).toISOString() });
        },
        error: () => { if (this.session === session) this.ui?.setError(humanChatLabel(this.cfg?.language, 'unavailable')); },
        token: token => {
          if (this.session !== session) return;
          session.token = token;
          this.persistHumanRecovery(state.conversationId);
        },
        retry: (retry, accepted) => {
          if (this.session !== session || this.humanChat !== channel) throw new Error('CHAT_NOT_ACTIVE');
          const previous = this.humanSendRetry;
          this.humanSendRetry = retry;
          if (accepted && previous) this.ui?.clearSentDraft(previous.text);
          this.humanDraft = this.ui?.getDraft() || '';
          const persisted = this.persistHumanRecovery(state.conversationId);
          this.updateHumanSendControls();
          if (retry && !persisted) throw new Error('CHAT_RECOVERY_UNAVAILABLE');
        },
        commands: commands => {
          if (this.session !== session || this.humanChat !== channel) throw new Error('CHAT_NOT_ACTIVE');
          this.humanCommandRetries = commands;
          this.humanCancelCommand = commands.find(command => command.action === 'customer-cancel')?.commandId || null;
          this.humanEndCommand = commands.find(command => command.action === 'customer-end')?.commandId || null;
          const persisted = this.persistHumanRecovery(state.conversationId);
          this.ui?.setHumanQueueCancel(humanChatLabel(this.cfg?.language, 'cancel'), this.canCancelHumanQueue(), this.humanCancelBusy);
          if (commands.length && !persisted) throw new Error('CHAT_RECOVERY_UNAVAILABLE');
        },
        returnReady: next => { if (this.session === session) void this.resumeHumanChat(next); },
      }, undefined, undefined, reloaded ? 0 : state.initialSequence, this.humanSendRetry, this.humanCommandRetries);
      this.recoveredConversationId = null;
      this.humanReloadRecovery = false;
      this.humanChat = channel;
      await channel.start();
    } catch {
      if (this.session === session) this.ui?.setError(humanChatLabel(this.cfg?.language, 'unavailable'));
    } finally { this.humanStarting = false; }
  }

  private async resumeHumanChat(state: HumanChatState): Promise<void> {
    const session = this.session;
    const channel = this.humanChat;
    if (!session || !channel || this.humanReturning || !state.conversationId) return;
    this.humanReturning = true;
    try {
      const resumed = await this.api.chatCustomer<{ token: string; exp: number; sessionId: string; callId: string; modality: string; nearRealtime: boolean }>(
        session.token, { action: 'resume', conversationId: state.conversationId });
      if (this.session !== session || this.humanChat !== channel) return;
      if (typeof resumed.token !== 'string' || !resumed.token || !Number.isSafeInteger(resumed.exp) || resumed.exp * 1000 <= Date.now()
        || !/^nrt-return-[a-f0-9]{64}$/.test(resumed.sessionId) || !resumed.callId || resumed.modality !== 'text' || resumed.nearRealtime !== true) throw new Error('CHAT_INVALID_RESUME');
      const returnedSession = { token: resumed.token, sid: resumed.sessionId, callId: resumed.callId, modality: 'text' as const, nearRealtime: true };
      const ownsSession = await this.claimHumanSession(returnedSession.sid);
      if (this.session !== session || this.humanChat !== channel) return;
      if (!ownsSession) throw new Error('CHAT_RECOVERY_LOCKED');
      if (!this.testerMode && !this.humanRecoveryStore()?.replace({ token: returnedSession.token, sid: returnedSession.sid,
        callId: returnedSession.callId, checking: true })) throw new Error('CHAT_RECOVERY_UNAVAILABLE');
      await channel.stop();
      if (this.session !== session) return;
      this.humanSendRetry = undefined;
      this.humanCommandRetries = [];
      this.humanEndBusy = false;
      this.humanDraft = '';
      this.session = returnedSession;
      this.lastSession = returnedSession;
      this.endBeaconSent = false;
      this.humanChat = null;
      this.humanState = null;
      this.humanMode = false;
      this.humanPreparing = false;
      this.humanSending = false;
      this.humanCancelCommand = null;
      this.humanEndCommand = null;
      this.ui?.resumeIvaText();
    } catch (error) {
      if (this.session === session) this.ui?.setError(humanChatLabel(this.cfg?.language,
        error instanceof Error && error.message === 'CHAT_RECOVERY_LOCKED' ? 'sessionLocked' : 'unavailable'));
    } finally { this.humanReturning = false; }
  }

  private async teardownSession(reason: string): Promise<void> {
    this.startupAttempt = null;
    this.humanRestoreAttempt = null;
    const sessionLocks = this.sessionLocks;
    this.sessionLocks = null;
    this.humanRecoveryStore()?.clear();
    this.humanSendRetry = undefined;
    this.humanCommandRetries = [];
    this.humanEndBusy = false;
    this.humanDraft = '';
    this.ui?.setHumanSendRetry('', false);
    this.recoveredConversationId = null;
    this.humanReloadRecovery = false;
    const sess = this.session;
    const wasHuman = this.humanMode || this.humanPreparing;
    const channel = this.humanChat;
    this.humanChat = null;
    this.humanState = null;
    this.humanEndCommand = null;
    this.humanCancelCommand = null;
    this.humanCancelBusy = false;
    this.session = null;
    this.humanMode = false;
    this.humanPreparing = false;
    if (this.humanPreparationTimer) clearTimeout(this.humanPreparationTimer);
    this.humanPreparationTimer = null;
    this.humanSending = false;
    this.pendingDirectResponses = 0;
    this.ui?.hideTyping();
    this.partialAgent = '';
    this.pendingUserEchoes = [];
    this.nearRtRelayedContext = {};
    if (this.typingIdleTimer) { clearTimeout(this.typingIdleTimer); this.typingIdleTimer = null; }
    this.typingShownAt = 0;
    this.toolsInFlightIds.clear();
    this.discardAgentAudio();
    this.userClipChunks = [];
    this.pendingUserClipUrl = null;
    this.lastAgentMsgId = null;
    this.agentAudioActive = false;
    this.clearMicRestoreTimer();
    this.droppedResponseId = null;
    this.currentAudioResponseId = null;
    this.localWorkingFiller?.dispose();
    this.localWorkingFiller = null;
    this.nearRealtimeWorkingFiller = null;
    this.workingFillerAudioActive = false;
    this.ui?.hideAgentStop();
    this.stopUrlAudio();
    try { await channel?.stop(); } catch { /* */ }
    try { await this.audio?.stop(); } catch { /* */ }
    this.audio = null;
    this.ui?.setMicRecording(false);
    this.ui?.setVoiceAvatarActive(false);
    this.ui?.setVoiceAvatarState('idle');
    try { await this.transport?.stop(); } catch { /* */ }
    this.transport = null;
    if (sess && !wasHuman) {
      try {
        if (sess.nearRealtime) await this.api.nearRealtimeEnd(sess.token);
        else await this.api.endSession(sess.token, sess.callId, reason);
      }
      catch { /* */ }
    }
    sessionLocks?.release();
    this.relay('session.ended', { reason, sid: sess?.sid, callId: sess?.callId });
  }
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // result is "data:<mime>;base64,<payload>" — strip the prefix.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('file_read_failed'));
    reader.readAsDataURL(file);
  });
}

// Auto-init from a <script data-widget-key="..." data-embed-secret="..." [data-api-base="..."]> tag.
function autoInit(): void {
  const current = (document.currentScript || (function () {
    const s = document.querySelectorAll('script[data-widget-key]');
    return s[s.length - 1] as HTMLScriptElement | null;
  })()) as HTMLScriptElement | null;
  if (!current) return;
  const publicKey = current.getAttribute('data-widget-key');
  if (!publicKey) return;
  const apiBaseUrl = current.getAttribute('data-api-base') || undefined;
  const language = current.getAttribute('data-language') || undefined;
  // Tester mode — a pre-issued dev token JSON payload was injected by the
  // admin tester. Skips the embedSecret + Origin checks.
  const devTokenRaw = current.getAttribute('data-dev-token');
  if (devTokenRaw) {
    try {
      const devTokenResponse = JSON.parse(devTokenRaw) as IssueJwtResponse;
      const testerOrigin = current.getAttribute('data-tester-origin') || undefined;
      init({ publicKey, devTokenResponse, apiBaseUrl, testerOrigin });
      return;
    } catch (err) {
      console.warn('[AutoQM widget] invalid data-dev-token payload:', err);
      return;
    }
  }
  const embedSecret = current.getAttribute('data-embed-secret');
  if (!embedSecret) return;
  init({ publicKey, embedSecret, apiBaseUrl, language });
}

export function init(opts: InitOptions): Promise<void> {
  const ctl = new WidgetController(opts);
  return ctl.init();
}

/**
 * Default API base URL for the widget. If the embedding page didn't pass
 * `data-api-base`, fall back to the origin of the *script* that loaded us
 * (e.g. `https://app.autoqm.com`) rather than the embedding page's origin
 * (e.g. `https://customer-site.com`) — the backend lives where the script
 * is served, not where it's embedded.
 */
function resolveDefaultApiBase(): string {
  try {
    const tag = (document.currentScript
      || document.querySelector('script[data-widget-key]')) as HTMLScriptElement | null;
    if (tag?.src) {
      const u = new URL(tag.src, window.location.href);
      return u.origin;
    }
  } catch { /* fall through */ }
  return window.location.origin;
}

// Run autoInit on DOMContentLoaded so callers can drop the script anywhere.
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit, { once: true });
  } else {
    autoInit();
  }
}
